import { useState, useRef } from "react";

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (n, decimals = 0) =>
  n == null || isNaN(n)
    ? "—"
    : new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(n);
const fmtDollar = (n) => (n < 0 ? `-$${fmt(Math.abs(n))}` : `$${fmt(n)}`);
const fmtPct = (n) => (isNaN(n) ? "—" : `${(n * 100).toFixed(2)}%`);
const cls = (...args) => args.filter(Boolean).join(" ");

// ─── amortization engine ─────────────────────────────────────────────────────
function buildAmortization(loanAmount, annualRate, periods = 360) {
  const monthlyRate = annualRate / 12;
  const pmt =
    monthlyRate === 0
      ? loanAmount / periods
      : (loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, periods))) /
        (Math.pow(1 + monthlyRate, periods) - 1);

  let bal = loanAmount;
  const rows = [];
  for (let i = 1; i <= periods; i++) {
    const interest = Math.round(bal * monthlyRate * 100) / 100;
    const principal = pmt - interest;
    bal = bal - principal;
    rows.push({ month: i, principal, interest, balance: Math.max(0, bal) });
  }
  return { pmt, rows };
}

// cumulative principal paid through month N
function cumulativePrincipal(rows, throughMonth) {
  return rows.slice(0, throughMonth).reduce((s, r) => s + r.principal, 0);
}

// ─── core calculation ────────────────────────────────────────────────────────
function calculate(inputs) {
  const {
    purchasePrice,
    downPaymentPct,
    interestRate,
    closingCostPct,
    rehabExpenses,
    acquisitionsFee,
    monthlyRent: monthlyRentRaw,
    propertyType,
    unitRents,
    monthlyTaxes,
    monthlyInsurance,
    monthlyHOA,
    propertyMgmtPct,
    vacancyRepairsPct,
    annualRentIncrease,
    appreciationYr12,
    appreciationYr35,
    closingCostsOnSale,
  } = inputs;

  const downPayment = purchasePrice * downPaymentPct;
  const estimatedClosingCosts = purchasePrice * closingCostPct;
  const totalPropertyInvestment =
    downPayment + estimatedClosingCosts + rehabExpenses + acquisitionsFee;
  // Effective monthly rent: sum of occupied units for multifamily, single field for SFR
  const unitCount = propertyType === "duplex" ? 2 : propertyType === "triplex" ? 3 : propertyType === "fourplex" ? 4 : 1;
  const monthlyRent = propertyType === "sfr"
    ? monthlyRentRaw
    : unitRents.slice(0, unitCount).reduce((s, r) => s + (Number(r) || 0), 0);

  const loanAmount = purchasePrice - downPayment;

  const { pmt: monthlyPI, rows: amorRows } = buildAmortization(
    loanAmount,
    interestRate
  );
  const pitiMonthly = monthlyPI + monthlyTaxes + monthlyInsurance;
  const dscrRatio =
    (monthlyRent * 12) /
    ((propertyMgmtPct * monthlyRent * 12) + pitiMonthly * 12);

  const capRateNOI =
    monthlyRent * 12 -
    monthlyTaxes * 12 -
    monthlyInsurance * 12 -
    monthlyHOA * 12 -
    monthlyRent * 12 * propertyMgmtPct -
    monthlyRent * 12 * vacancyRepairsPct;
  const capRate = capRateNOI / purchasePrice;

  // 5-year projections
  const years = [];
  let grossRent = monthlyRent * 12;

  // property value progression for appreciation
  const propValues = [purchasePrice]; // index 0 = purchase, 1 = end of yr1, etc.
  for (let yr = 1; yr <= 5; yr++) {
    const rate = yr <= 2 ? appreciationYr12 : appreciationYr35;
    propValues.push(propValues[yr - 1] * (1 + rate));
  }

  for (let yr = 1; yr <= 5; yr++) {
    if (yr > 1) grossRent = grossRent * (1 + annualRentIncrease);
    const taxes = monthlyTaxes * 12 * Math.pow(1.04, yr - 1);
    const insurance = monthlyInsurance * 12 * Math.pow(1.04, yr - 1);
    const hoa = monthlyHOA * 12; // HOA is flat annually (no growth)
    const propMgmt = grossRent * propertyMgmtPct;
    const vacancy = grossRent * vacancyRepairsPct;
    const noi = grossRent - taxes - insurance - hoa - propMgmt - vacancy;
    const annualPI = monthlyPI * 12;
    const grossProfit = noi - annualPI;
    const monthlyCashFlow = grossProfit / 12;
    const cocROI = grossProfit / totalPropertyInvestment;

    // principal reduction: cumulative for display, annual for CoC+PR ROI (matches spreadsheet)
    const principalPaid = cumulativePrincipal(amorRows, yr * 12);
    const principalPaidPriorYear = yr > 1 ? cumulativePrincipal(amorRows, (yr - 1) * 12) : 0;
    const annualPrincipalPaid = principalPaid - principalPaidPriorYear;
    const cocPlusPR = (grossProfit + annualPrincipalPaid) / totalPropertyInvestment;

    // appreciation
    // appreciation tracked via appreciationThisYr below
    const appreciationThisYr = yr === 1 ? 0 : propValues[yr] - propValues[yr - 1];
    const appreciationROI = appreciationThisYr / totalPropertyInvestment;

    // cumulative equity gain
    const cumulativeEquity = yr === 1 ? 0 : propValues[yr] - purchasePrice;

    // loan balance at end of year
    const loanBalance = amorRows[yr * 12 - 1]?.balance ?? 0;
    const proceeds =
      propValues[yr] * (1 - closingCostsOnSale) - loanBalance;

    // depreciation
    const depreciation = purchasePrice * 0.010182;
    const deprecROI = depreciation / totalPropertyInvestment;

    const totalROI =
      (grossProfit + annualPrincipalPaid + depreciation + appreciationThisYr) /
      totalPropertyInvestment;

    years.push({
      yr,
      grossRent,
      taxes,
      insurance,
      hoa,
      propMgmt,
      vacancy,
      noi,
      annualPI,
      grossProfit,
      monthlyCashFlow,
      cocROI,
      principalPaid,
      annualPrincipalPaid,
      cocPlusPR,
      appreciation: appreciationThisYr,
      appreciationROI,
      cumulativeEquity,
      proceeds,
      depreciation,
      deprecROI,
      totalROI,
    });
  }

  // Return on Investment summary rows (averages matching spreadsheet)
  const roiRows = {
    cocAvg12: (years[0].cocROI + years[1].cocROI) / 2,
    cocAvg3: years[2].cocROI,
    cocAvg4: years[3].cocROI,
    cocAvg5: years[4].cocROI,
    cocPRAvg12: (years[0].cocPlusPR + years[1].cocPlusPR) / 2,
    cocPRAvg3: years[2].cocPlusPR,
    cocPRAvg4: years[3].cocPlusPR,
    cocPRAvg5: years[4].cocPlusPR,
  };

  return {
    downPayment,
    estimatedClosingCosts,
    totalPropertyInvestment,
    monthlyRent,
    loanAmount,
    monthlyPI,
    pitiMonthly,
    dscrRatio,
    capRate,
    years,
    roiRows,
  };
}

// ─── Input field component ───────────────────────────────────────────────────
function Field({ label, value, onChange, type = "dollar", hint }) {
  const [focused, setFocused] = useState(false);
  const [rawText, setRawText] = useState("");

  const displayValue = focused
    ? rawText
    : type === "dollar"
    ? value === 0
      ? "$0"
      : `$${fmt(value)}`
    : type === "percent"
    ? `${(value * 100).toFixed(2)}%`
    : String(value);

  const handleFocus = () => {
    // Show the numeric value ready to edit (percent as e.g. "8.00", dollar as "8500")
    if (type === "percent") {
      setRawText(value === 0 ? "" : String(parseFloat((value * 100).toFixed(4))));
    } else {
      setRawText(value === 0 ? "" : String(value));
    }
    setFocused(true);
  };

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    setRawText(raw);
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      onChange(type === "percent" ? num / 100 : num);
    } else if (raw === "" || raw === ".") {
      onChange(0);
    }
  };

  return (
    <div className="field-wrapper">
      <label className="field-label">{label}</label>
      {hint && <span className="field-hint">{hint}</span>}
      <input
        className="field-input"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={() => { setFocused(false); setRawText(""); }}
      />
    </div>
  );
}

// ─── Metric card ─────────────────────────────────────────────────────────────
function MetricCard({ label, value, sub, highlight }) {
  return (
    <div className={cls("metric-card", highlight && "metric-card--highlight")}>
      <div className="metric-value">{value}</div>
      <div className="metric-label">{label}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  );
}

// ─── Section header ──────────────────────────────────────────────────────────
function SectionHeader({ title, subtitle }) {
  return (
    <div className="section-header">
      <div className="section-title">{title}</div>
      {subtitle && <div className="section-subtitle">{subtitle}</div>}
    </div>
  );
}

// ─── Year table ──────────────────────────────────────────────────────────────
function YearTable({ years }) {
  const rows = [
    { label: "Gross Rent", key: "grossRent", format: fmtDollar },
    { label: "Taxes", key: "taxes", format: fmtDollar },
    { label: "Insurance", key: "insurance", format: fmtDollar },
    { label: "HOA", key: "hoa", format: fmtDollar },
    { label: "Property Management", key: "propMgmt", format: fmtDollar },
    { label: "Vacancy / Repairs", key: "vacancy", format: fmtDollar },
    {
      label: "Net Operating Income",
      key: "noi",
      format: fmtDollar,
      bold: true,
    },
    {
      label: "Annual Principal & Interest",
      key: "annualPI",
      format: fmtDollar,
    },
    {
      label: "Gross Profits",
      key: "grossProfit",
      format: fmtDollar,
      bold: true,
      highlight: true,
    },
    {
      label: "Monthly Cash Flow",
      key: "monthlyCashFlow",
      format: fmtDollar,
      bold: true,
    },
    { label: "Cash on Cash ROI", key: "cocROI", format: fmtPct },
    {
      label: "Cash on Cash + Principal Reduction",
      key: "cocPlusPR",
      format: fmtPct,
    },
  ];

  return (
    <div className="year-table-wrapper">
      <table className="year-table">
        <thead>
          <tr>
            <th className="year-table-label-col"></th>
            {years.map((y) => (
              <th key={y.yr} className="year-table-year-col">
                Year {y.yr}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.key}
              className={cls(
                "year-row",
                i % 2 === 0 && "year-row--even",
                row.bold && "year-row--bold",
                row.highlight && "year-row--highlight"
              )}
            >
              <td className="year-row-label">{row.label}</td>
              {years.map((y) => {
                const val = y[row.key];
                const formatted = row.format(val);
                const negative = val < 0;
                return (
                  <td
                    key={y.yr}
                    className={cls(
                      "year-row-value",
                      negative && "year-row-value--neg"
                    )}
                  >
                    {formatted}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Appreciation table ──────────────────────────────────────────────────────
function AppreciationTable({ years }) {
  const rows = [
    { label: "Principal Reduction", key: "principalPaid", format: fmtDollar },
    { label: "Appreciation", key: "appreciation", format: fmtDollar },
    { label: "Appreciation ROI", key: "appreciationROI", format: fmtPct },
    {
      label: "Cumulative Equity Gain",
      key: "cumulativeEquity",
      format: fmtDollar,
      bold: true,
    },
    {
      label: "Proceeds in Event of Sale",
      key: "proceeds",
      format: fmtDollar,
      bold: true,
      highlight: true,
    },
    { label: "Deprec / Tax Savings", key: "depreciation", format: fmtDollar },
    { label: "Depreciation ROI", key: "deprecROI", format: fmtPct },
  ];

  return (
    <div className="year-table-wrapper">
      <table className="year-table">
        <thead>
          <tr>
            <th className="year-table-label-col"></th>
            {years.map((y) => (
              <th key={y.yr} className="year-table-year-col">
                Year {y.yr}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.key}
              className={cls(
                "year-row",
                i % 2 === 0 && "year-row--even",
                row.bold && "year-row--bold",
                row.highlight && "year-row--highlight"
              )}
            >
              <td className="year-row-label">{row.label}</td>
              {years.map((y) => {
                const val = y[row.key];
                const formatted = row.format(val);
                const negative = val < 0;
                return (
                  <td
                    key={y.yr}
                    className={cls(
                      "year-row-value",
                      negative && "year-row-value--neg"
                    )}
                  >
                    {formatted}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Default inputs ──────────────────────────────────────────────────────────
const DEFAULTS = {
  address: "",
  dwelling: "",
  yearBuilt: "",
  sqft: "",
  bedBath: "",
  purchasePrice: 0,
  downPaymentPct: 0.25,
  interestRate: 0,
  closingCostPct: 0.04,
  rehabExpenses: 0,
  acquisitionsFee: 0,
  propertyType: "sfr",   // sfr | duplex | triplex | fourplex
  monthlyRent: 0,
  unitRents: [0, 0, 0, 0],  // per-unit rents for multifamily
  monthlyTaxes: 0,
  monthlyInsurance: 0,
  monthlyHOA: 0,
  propertyMgmtPct: 0.0,
  vacancyRepairsPct: 0.05,
  annualRentIncrease: 0.03,
  appreciationYr12: 0.03,
  appreciationYr35: 0.06,
  closingCostsOnSale: 0.06,
};

// ─── Main App ────────────────────────────────────────────────────────────────
const LOGO_SRC = "data:image/png;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAUDBAQEAwUEBAQFBQUGBwwIBwcHBw8LCwkMEQ8SEhEPERETFhwXExQaFRERGCEYGh0dHx8fExciJCIeJBweHx7/2wBDAQUFBQcGBw4ICA4eFBEUHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh7/wAARCACfAWQDASIAAhEBAxEB/8QAHQABAAIDAAMBAAAAAAAAAAAAAAcIBQYJAgMEAf/EAFIQAAEDAwICBQQJEgUDAwUAAAEAAgMEBQYHERIhCBMxQVEUImFxFRg3dYGRlLPRCRYjMjZCUlVWYnN0gpOhscHhFzRXctMkMzhDU2SDkrLC0v/EABkBAQADAQEAAAAAAAAAAAAAAAAEBQYHAv/EADURAAIABAEHCwQDAQEAAAAAAAABAgMEBSEGERIWMUFxUVJTYZGhscHR4fATMzRyFGKBIiP/2gAMAwEAAhEDEQA/AKZIiIAiIgCIiALP4ViF+y+4GkstGZAzbrZnnhiiB73O/p2nwXv07w26ZpfW2+gaY4GbOqalw8yFnifEnuHf8atxiOO2vF7HDaLTB1cEY3c4/bSO73OPeSru02eKtenHhAu/gZ693yGgX05eMx93H0IktHR5ohADd8iqHzEc20sIa0H1u3J+IL0X/o9NEDn2LIHOlA82KsiADv2m9nxKeUWrdjoXDo6He85jFlFcVHpfU7lm8Cj2T4/d8auj7beaKSlqG8wHc2vH4TT2EekLFq6GomG2vNLE6317RHOzd1NUtHnwv8fSD3jv+JVGy7Hbpi98mtF2gMc8Z3a4fayN7nNPeCsjdbTHQxZ1jA9j8mbizXqC4QaMWEa2rzXzAxCIipy8CIiAIiIAiIgCIiAIiIAiIgCIiAIiIAiIgCIiAIiIAiIgCIiAIiIAiIgCIiAIiIAiIgC2HAcRumZX6O2W1nC0bOqJ3DzIWfhH+g7168HxW65ffYrVa4tyfOmmcPMhZ3ucf6d6t1guKWvD7DHarZH+dNM4efM/vc7+g7ldWi0xVsenHhAu/qRQXu9Q0EGhBjMezq635HnheMWvE7FFabVFwxt86SRw8+Z/e5x8f5diytbVU1FSS1lZPHBTwsL5JHu2axo7SSvKqqIKWmkqamZkMETS+SR7tmtaO0kqrmteps+XVjrTanvhscL+Xcalw+/d+b4D4Tz7NfXV0m2yUksdy+bjD263z7rUNt4bYovm8auaq3LJrk6jslVUUNmhJDOreWPqD+G/bnt4N+PmsvopqzUWurZYsprJJ7fK7aGrmcXOp3eDieZZ/L1KGkWHhulTDUfyNLHu4cDocdmpIqb+No/89+flz8pfRjmvYHscHNcNwQdwR4rVtS8ItmbWM0dWBDVxAupaoN3dE7wPi094/qoc0K1TNpfDjOR1BNvcQ2kqnn/LnuY4/genu9XZYwEEAgggjcEd63VLVSLnTvDOntXJ83M5zWUlTaalY5msU1v+b0UeyixXPG71PaLtTmGphP7L29zmnvB8VjFcbVDBLbm9lNPPwwV8IJpKoDmw/gnxae8fCFUrIrLccfvE9putO6Cqgds5p7CO5wPeD3FYq62uOhjzrGB7H5M6BZrxBcZeZ4RravNdXgY5ERVJdBERAEREAREQBERAEREAREQBERAEREAREQBERAEREAREQBERAEREAREQBERAFmcNxq6ZXfIbTaoeOV/N7z9pEzvc49wC8MTx66ZPe4bRaYDLPKeZP2sbe9zj3AK3GnOF2vCrG2goWiSok2dVVJGzpn/0aO4f1VxabVHWx54sIFtfkiivV5gt8vRhxjexcnW/mJ7dP8QteGWJlttzOKR2zqiocPPmf4n0eA7ln55YoIXzzyMiijaXPe87NaBzJJ7gv2WRkUTpZXtZGwFznOOwaB2knwVZtcNUZMjmksFhmcyzRu2llbyNU4f/AKeA7+3wWyrKyRbZCzLgvnezB0NDUXaoePXE3u+bkenW7U+XKal9kssr47JE7znjkapw7z+aO4fCe7aK0Rc9qqqZVTHMmPO2dQo6SVRylKlLMl39bCIijkkKcNCNVPIjBi2S1H/SnZlFVyH/ALXhG8/g+B7uzs7IPRSqOsmUk1TJb9yFX0MqukuVNXB8j5UX1Wl6rYBb84s/A7gp7pA0+S1W3Z+Y7xaf4do9Ma6Eaq9X1GLZNU+ZyZQ1kh+18I3nw8D8B7lPy6FIn09zp3hnT2rk+bmcyqaeptFUsczWx8q+bUUYvtpuFjus9rulM+nqoHcL2O/mPEHuK+FW81b09oc3tXHHwU93p2nyaoI5O/Mf4tP8Pj3qdeLbXWi5z225Uz6aqp3lkkbxzB/qPT3rD3S2R0Mzlhex+XE6FZ7vLuMrkjW1ea6j5ERFVlwEREAREQBERAEREAREQBERAEREAREQBERAEREAREQBERAEREAREQBZLGbHcsivVPaLVTmaqnOwHc0d7nHuA7ysarP9GzFYbRh4v08Y8uunnBxHNkIPmgesji+LwVjbKF1s9S9218Cru9xVvpnM2t4LibXpng9swiyCkpQ2atlANXVFvnSu8B4NHcP6ranuaxhe9wa1o3JJ2AHiv0kAEkgAdpKrlrrqobs+bGccqCLe08NXVMP+YPe1p/A9Pf6u3dVVTItlOsMyWxcvzeznVJSVN2qXjnbxbe75uR6Nc9UnX2WXHMenLbUw8NRUNOxqiO4fmfz9Sh5EXPaurmVc1zJjx8Dp1FRSqKUpUpYePWwiIoxLCIiAIiIAp/0H1U60QYrk1T9k5MoayR323hG8nv8AA/Ae5QAgJB3HIqZQ1syjmqZL/wBXKQbhb5VfJcqZ/j5GX1Wgawac0ea2zyimEdPeqdn2Cc8hIP8A23+jwPd8a1DQnVTy9sGL5LUf9WAGUdXIf+6O6N5/C8D39nb2zcugSptPdKblT2remcymyqq0VXJEtj3NehRO50NZbLhPQV9PJT1UDyyWN42LSF8ys50jcOt9zxSoyaKNsVytzWl0gG3XRcQBa7x233B+BVjWCuVBFQzvpt51tXA6RarlDcKdTUszWDXWERFALMIiIAiIgCIiAIiIAiIgCIiAIiIAiIgCIiAIiIAiIgCIiAIiIArv4ZEyDD7LDG3hYyggAH/02qkCvFin3K2j9Rg+batVkt9yZwRjMsX/AOcri/I1nXi4VNt0tu01LIY5JQyAuB5hr3hrv4Ej4VUNWw6RvuT3H9NB84FU9R8pm/5UK/qvFkrJKFKiif8AZ+CCIizpqQiIgCIiAIiIAiIgP1pLXBzSQQdwR3KetLtbqWmtbLXmLqgywjhirmM4+No7A8Dnv6Rvv3+JgRFMo66dRx6cp+jINfbpFfL0Jy4PeiataNXLfkNjkx7HI5nU05aamplbwcTQQQxre3tA3J8NlCqIvNZWTauZ9Sa8T1Q0MmhlfSkrDxCIiikwIiIAiIgCIiAIiIAiIgCIiAIiIAiIgCIiAIiIAiIgCIiAIiIArxYp9yto/UYPm2qjqvFin3K2j9Rg+batVkt9yZwRjMsftyuL8jTekb7k9x/TQfOBVPVsOkb7k9x/TQfOBVPUbKb8tfqvFkvJL8GL9n4IIiLPGoCIiAkaLQzV6WJksent9cx7Q5rhT8iD2HtXl/gRrD/p3fvk/wDdXqs/SQ0VgtFHDLnNM2SOnY1w8ln5ENAI+0Uu2m4Ul1tVJdLfMJ6OsgZPBKAQHxvaHNdsefMEHmgOXH+BGsP+nd++T/3WjZBZ7pj95qbNeqGahuFK/gnp5m7PjdsDsR6iF04vXSC0fst4rLRc8zp6euop309REaaclkjHFrm7hmx2IPYufHSLv1pyfWzKL9YqxtbbayrD6edrXND28DRvs4A9oPaEBo9toqu5XGmt1BTvqKuqlbDBEwbuke47NaPSSQFIH+BGsP8Ap3fvk/8AdYXRf3X8O9/KP55i6uZDdaWxWC4Xuu6w0tvppKqbq27u4GNLnbDvOwKA5c1Oh2r1PC6WTTvIeFo3PBSOcfiG5Wh3CirLdWSUdwpJ6SpiPDJDPGWPYfAtPMLpTp30mdLs4ymkxu2VdypLhWO4KYVtL1bJX9zA4OIBPdvtv2dqx/TO0xs+ZaU3XImUUUd/sVM6rp6trAHviZzkice9pbxEb9hA279wObyIiAy2J41fssvDbPjdpqrpXuY6QQU7OJ/C0bk+oLYsi0i1Nx2zVF5veE3mgt9M0OnqJYNmRgkAEnu5kK0f1OTBvJ7NfNQauHaSrf7HULiP/TYQ6Vw9Bdwj9gq1WV2SiyXGbnj9yYH0dxpZKaYbfevaQSPSN90Bx6RZbMLFW4vlV0x24sLau21clNLy7SxxG49B23HoKxKA27D9M8+zC1vueL4rcrtRslMLpqaPia14AJafTsR8a+PNcIy7CpqaHK8fr7O+qa50Aqo+HrA0gHb1bj41Yj6nXm3sZnd2wiqm2p7zT+U0rSeQqIgdwPS5hJ/YCmnp7YX9cmi7r7TxcdbjtQKoEDmYHeZKPVza79hAc9bZQ1lzuVNbbfTS1VZVSthghjbu6R7js1oHeSSt9l0M1eiifLLp9fWMYC5zjBsAB2k81v8A0CsL+uXWlt8qIuOix2nNWSRy693mRD183O/YVrumTm/1laGXbyebq7heNrbS7HYjrAescPVGH/CQgOZiz2F4blOZ1s9HitjrbvUU8fWyx0zOIsZvtufRuVgVav6m17pGT+87fnmICHP8CNYf9O798n/uh0J1hAJOnl+2H/x/7rovrJqjjWlNio7zk8dfJTVdT5NGKOFsjuPhc7mC4ctmlRTJ0yNJXRuaKXJtyCP8jH/yIDnxIx0b3MeC1zSQQe4r67Harje7vS2i00ctbX1cgiggiG75HHsAHivRWyNmrJ5mb8L5HOG/gTupB6Mf/kDhHvtF/NAfv+BGsP8Ap3fvk/8Adems0R1cpKd082neRdWwbuLKNzyB6m7ldP8AM8ht+J4pc8luomNDbad1ROIWcT+Bo3Ow3G5+FRvph0jdM9Qsnixuy1lwprlO1xp4q2m6sTFo3LWuBI32BOx27OSA5l1dNUUlTJTVcEtPPE4tkilYWuYR3EHmCvUuhPTo0ws2RaY1ucUtHFDf7I1srqhjQHVFPxBr2PP33CDxAns2I71z2QHlGx8kjY42Oe95DWtaNySewAKUsf6PGst7omVlHgtfFC9vE01b46dxH+2Rwd/BS19Tqwu03nK79l1ypoqmosrIYqFsjQ4RSS8ZMgH4QDNge7iKsd0iNc7To4y0srrFX3ae59YY2wvEbGNZw77vIPPzhsAPiQHPTPNLtQMFjE2V4pcbbTl3CKhzA+Enw6xhLd/RutOXVbC9QsC1Q04guk89A23XWF8VRb7lLGHN2Ja9j2k7HmDz7xsVzc1vxi34dqtkGO2ipZU26lqt6SRkgkBie0PYOIdpAcAfSEBpikSm0O1cqaeKog0/vskUrA+N7afk5pG4I+BR2uwWGfcfZfe+D5tqA5i/4Eaw/wCnd++T/wB1rWa4Nl+FPpWZXj1fZ3VYcacVUfD1gbtxberiHxq+N26XmldsutXbamlyTrqSd8EnDRRkcTHFp2PWdm4VbOmNrDierVXjUuLRXKNtsjqGz+WQNj3Mhj4eHZzt/tCgK/IiIArxYp9yto/UYPm2qjqvFin3K2j9Rg+batVkt9yZwRjMsftyuL8jTekb7k9x/TQfOBVPVsOkb7k9x/TQfOBVPUbKb8tfqvFkvJL8GL9n4IIiLPGoCIiALrdo77kmH+8VF8wxckV1u0d9yTD/AHiovmGIDmJrr7tWbe/9b8+9aYtz1192rNvf+t+fetMQG3aL+6/h3v5R/PMXVPNrO/IcOvVhjnbTvuVBPSNlc3iDDJGW8RHftvuuVmi/uv4d7+UfzzF1TzW5VFmw293ekEbqiht9RUxB43aXsjc4bjw3CArTo/0QTh+oFqym9ZjHcY7XUNqYaamozHxyNO7eJxcdgDsdgOeylrpV5fbsQ0NySatnjbUXGjkt1HET50sszSzYDv2BLj6Atb6IuuFXq3Z7rR5DHQ02QW2QPMdKxzGS07uTXhriTuHAg8+9viox+qL4Jcqims+oNJUVU9FSgUNbTmQujg4iSyVrd9m8R81xHfwICli+i3UdTcbhTW+jidNU1MrYYY29r3uIDQPWSF86n/oJ4P8AXVrTDeamHjoMdi8teSORmPmwj177u/YQF69PrBbdNNKbZZXyMipLJbuKqm7AXNaXyyH1u4nfCoo6G2rdRqO3MaS6TudWQXaSupWPPNtJM48LB6GFpH7QU05tfMTstmd9eNztNDbawmncLlKxkU24O7Dx8nbgHl4LUMOyTQuhvUcOJXfBKS51pFNG23SU0cs3ERswcGxduduXjsgKp/VDcH9htRrfmlJDw0t9p+rqC0chUxADc/7mFn/2lVeXUDpaYN9feiF6oYIesuFvZ7I0Ww3PWRAktH+5nG31kLl+gM7p/klXh+bWbKKEnr7ZWR1AAP24afOb6iNwfWusLhaM1wgjzam03y3+sPhmj/8A5cuQK6H9AbNvrk0cOPVM3HW47UGm2J5mnfu+I/B57f2QgM/0Q9LqnTHA7lS3SLhulfdJnSuI5mGNxji+AhpeP0irV9UIzf2c1Ro8RpZuKkx+n+ygHkaiUBzviYIx8avZlt7osaxe55BcXhlJbqWSplO/3rGl23rO2y5G5Ze63JcnueQXF5fV3GqkqZjvv5z3Ekeob7IDGK1f1Nr3SMn952/PMVVFav6m17pGT+87fnmIC5+a4zieU2+Gjy60W250kUvWRR1zGuY1+xG4379iVFOr2lmkFv0qyuvtuG4xT1tPZ6qWnligjD2SNicWuaR3g7LPdJfSWo1fxO22OmvsdndR13lRlfTmUPHA5vDsHN2+2339CgD2j90PI6j023vW7/lQFPFI/Rj/APIHCPfaL+a0W/UBtV8r7W6USmjqZKcyAbcXA4t327t9lvXRj/8AIHCPfaL+aA6WapYzLmWnV+xWGrZRyXSikpmzvYXCMuG25AI3UB6F9E4YBqFQZfeMtZdH24ukpqanpDEDIWloc5xceQ3J2A7duanrVi/VuLaZ5JklubC+stltnqoGzNLmF7GEjiAIJG48VH3RO1km1cw6tlvDKSnyC21HBVw0zS1jo3c45GgkkA7Oaefa30oDw6aWX27GNB71Q1M7BXXuPyCjh3855cRxu28Gs3JPjt4rmkrd/VFMEucN9teoUE9VU22ojFDURveXMpZWjdhaOxrXjfs++b4uVREBM3RP1ji0jzWpkusE1RYbtGyGubEN3xFpJZK0d+3E4Ed4ce8BXzocj0m1asbaNlxxzJqSXzhSVBY+Rp27TE/z2u+AFU96DGLaa5vc8hx7NbFR3O5tjiqrf18j2kxjibKG8LhvsSw/CpZ156JtuvYtlbpVDa8dqabibU080koZMCQWvD/OIcOfLbnv6OYGb1N6IenGQUU02Kioxi57ExmKR0tM53g6NxJA/wBpG3gVQ7OMYu+G5ZccYvtOILjb5jFM0Hdp7w5p72kEEHwIXUrRHGskxDTS1Y/ll+9nLrStcJKric4cJcS1gc7znBoIG558lQXps3i3XnpEX2S2yMlZSxwUksjDuHSsjAfz8QfN/ZQEKrsFhn3H2X3vg+bauPq7BYZ9x9l974Pm2oDT6vSbRqpq5qiqwrFZKiWRz5Xvp4y5zidyT6d1SDpt49jWM6yQ27FbXQW2gNpglMNEwNjLy+QE7DlvsB8Sl7IOhdc7pf7jc26hU0IrKqWoEfsa48PG8u236zntuoX6R+g1Vo5b7NWVOTRXn2UlljDWUhh6vgDTvuXO334v4ICFkREAV4sU+5W0fqMHzbVR1XixT7lbR+owfNtWqyW+5M4IxmWP25XF+RpvSN9ye4/poPnAqnq2HSN9ye4/poPnAqnqNlN+Wv1XiyXkl+DF+z8EERFnjUBERAFYTHOlzqZYcet1jo7bjTqa30sVLC6SklLyyNga0kiUbnYDuVe0QGTyq9VeR5NdMgr2RMq7lVy1czYmkMD5HFzg0EkgbnxKxiIgMjjN3qcfyK232ibE6qt1VHVQtlBLC9jg4AgEEjceKna/dL3U682OvtFVbMZbT11NJTSmOklDg17S07EynnsVXhEBtmlGf5BppmMGUY2+DyyON8To52l0UrHDYteAQSN9j2jmApTy3pYahZTjNxx282TFZ7fcIHQTs8kl34SO0HreRB2IPcQFACIApV0V12yzSWz11txi2WOVtdOJ55qynkfI4huzW7te0cI57DbtcVFSICS9bda8x1bbbI8mZboIbaZHQxUMTmNc5+27ncTnbnZoA+FR3b6uot9fT19HK6KpppWzQyN7WPaQWkeogL0IgLJDpm6q8HC614q8bbHejl5+v7Kq63Gp8tuFTWdRDT9fK6XqoQQyPiJPC0EkgDfYcyvnRAFvmi+q2UaTXysu2MiilfWU/k88NZG58bgHBwOzXNPECDsd+8rQ0QE3andJzUXUDC63E7tT2Skoa0s691FTyMkc1rg7h3dI4bEgb8lCKIgC37RXVfJNJr1XXbGqa2zz1tMKeUVsTntDQ4O5Brm89wtBRAWU9ufqr+K8V+Rzf8qe3P1V/FeK/I5v+VVrRAfVd66a53asuVQGNmq53zyBg2aHPcXHb0blZDBskr8Py+15Pa44JK221DaiBs7S6Mub2cQBBI+ELCogJ+zHpYak5Til0xu427G2UdzpZKWd0NLKHhj2kEtJkIB2PgVGuj2peS6WZQ/IMZdTOnlp3U80NSxz4pWEg+cAQdwQCDv3LS0QE7Z/0o89zjELji1+suLy2+vi6uTgpJQ9h3Ba9pMp2cCAQdu0KCURAfdYLxdLBeKa8WWvqKC4UrxJBUQPLXsd6D/TvVjcY6Z+o1uomU95s1jvL2DbyhzHwSO9LuA8O/qaFWREBYfPul3qdkltlt1rjtuORStLXy0THOn2Pbs95PD6wAfSq9SPfJI6SR7nvcS5znHckntJK8UQBWMtvTE1RoLdTUENsxcxU0LIWF1JKSWtAA3+y9vJVzRAWU9ufqr+K8V+Rzf8qjvW3W/LtXKK2UmTUlogZbZJJITRQvYSXhoPFxPdv9qPBReiAIiIArmaTXWO86dWSsjcHObSsgk9D4xwEfw/iqZqV+j7qDDjFyksd4m6u1Vr+Jkrjygl7Nz+aRsD4bA+Ku7DWw01TmjeZRYehnspKCOrpc8tZ4oXn/zeWFzewQZPitfY539W2qj2bJtvwPBBa74CAqkZJgmV2G4Po62y1jyD5ssETpI5B4tcB/dXPa5r2h7XBzXDcEHcEeK/QSOwrV3K0Sq9qJvM1v6jGWq9zranBCtKF7uso57AX38S3L5K/wChPYC+/iW5fJX/AEK8m58Sm58SqrVaDpH2e5c64zOiXb7FG/YC+/iW5fJX/QnsBffxLcvkr/oV5Nz4lNz4lNVoOkfZ7jXGZ0S7fYo37AX38S3L5K/6E9gL7+Jbl8lf9CvJufEpufEpqtB0j7Pca4zOiXb7FG/YC+/iW5fJX/QnsBffxLcvkr/oV5Nz4lNz4lNVoOkfZ7jXGZ0S7fYo37AX38S3L5K/6E9gL7+Jbl8lf9CvJufEpufEpqtB0j7Pca4zOiXb7FD6ukqqOTqqummp37b8MrC0/EV6VcnVjHrdkOEXOKvijMtPTST08zh50T2NLgQfDlsfQqbKhultdBMUOlnTNJZ7srlKcWjotPFBeTmPbwlzXN4huNxtuPFeKlTLrTjNbYMLmu2Wi1VP1uxDqPY6Sbzevn2dxNO3PwVYW5Fr2OY7he0tOwOxG3bzC8Vu+t7IYtQ5o6efyiFlutzY5eAt6xooYNnbHmNxz2K+fSGlpqnNWPqKSKtfSUNZW09LK3iZPPDTySRsc374cTQS3v2270BrMtBXRUbKyWiqY6aT7SZ0Tgx3qdtsV8ykDEc/zavzShirLzcb3FcaqOnqbdVTOlgq43uDTEYj5uxB2GwHDyI2IC1PLqKkt2V3e32+XraOlrp4ad++/FG2RzWnfv3ACAxrWPc1zmtcWt5uIHIetGMe93CxrnO8ANypJ0Xu1LZsezirr6FlfQPt1NBWUzgN5IX1kLXhp+9eAd2u7nAFbXp1a7Ng+UCGkuFDe7hfaWsFBURkPFPbjSynrXD7yaTbh4TzYGv/AAgUBBK99LSVdVxeS0s8/D9t1cZdt69l6FKOl9V5Jpfk8n111mM73a3DyqlbKXP+x1fmHqyDt3+HJARlUQT08piqIZIZBzLXtLSPgK9ay2W1L6u/Tyvv1VfuTQ2uqOMPlAaO0PJcNuzn4LEoAvIMcWF4a7hB2LtuQK8VuVr9xi/+/wBbvmKtAaaiLddJWshr75eI6aKpr7RZpq2gjkYHtEwfGzrOE8ndW175ACCN2A9yA1OqoK+lhjmqqKpgilG8b5InNa/1EjmvmUmaY5ZlGQ55b8fvt4uN8td6qBS3Gkrah80bon8nSAOJ4XMG7w8bFpb27bqNpmtZM9rHcbQ4hrvEeKA/GMc94Yxpc5x2AA3JK8VJehdpvrJrxmtitlVXV1iga23NhhMhFbMS2N+233jRJJ62N8Vi9acfnseZGpfbZrbBeKdlzhppGFph63frItj2cEgkaPQB4oDSQCSABuT2BeXVydb1XVu6zi4eDbnv4beK+3Gvuitn63F/+YU6VFNT5VrtBlFvhjjutry5kF8po2gB8flnBFWNaO4+ayTbsdwu+/5AV7XtpaeeqnbBTQSzyv5NZGwucfUAlZ/m5v0jv5rddMr5BabNfqSorLtZW3DyeJt7t8Je6mLS93VP2LTwSDcnhcD9jHJwBCA0mohmpp3wVEUkMrDs5kjS1zT4EHsXrW86ouvU9vsFXcrxRZDRmGaGhvEJkMs7WvBdFKZAH8UZcNg4cg4bEjZaMgCIiAIiIAiIgN7wLVTKMSiZRwzMr7e3spanchg/McObfV2ehSTS9Ie3mIeVYzVNk7xFVNLf4tCr2isqe71lPDowR4dePiVNTZKGpi05kvHqzrwLFe2Gs35N1/79n0J7Yazfk3X/AL9n0KuqKRrBXc7uRG1Zt3Mfa/UsV7Yazfk3X/v2fQnthrN+Tdf+/Z9CrqiawV3O7kNWbdzH2v1LFe2Gs35N1/79n0J7Yazfk3X/AL9n0KuqJrBXc7uQ1Zt3Mfa/UsV7Yazfk3X/AL9n0J7Yazfk3X/v2fQq6omsFdzu5DVm3cx9r9SxXthrN+Tdf+/Z9Ce2Gs35N1/79n0KuqJrBXc7uQ1Zt3Mfa/Ul7UrWqpySxzWWz219up6lvDUSySh8jm97RsNgD3nnyUQoirqqrnVUenNedlpR0Uijl/Tkw5kFkLxd626xW+OscxzbfSNo6fhaBtG1znAHxO73c1j0UYln3367Vt7uPl9e5jp+phg3a3hHDFG2NnL/AGsavTa6+ttdxp7jbqmWlrKaRssM0TuF0bwdwQfFfMiA3Z+pV5b1lRQ2nHrbc5WlslzorayKp84bOLSPNjcQT5zGtPM81pKIgMhb7vW0Frudtp3sFPc4mRVILdyWskbI3Y93nNCY9d62xXaO529zG1MbJGNL2hw2exzHcj6HFY9EAWxYvltbYLZW2yO32q4UdbLFNLDX0omaHxh4Y4cxsQJHj4VrqID779c/Za4OrPIKCg3aG9TRQ9VGNu/h3PMr4ERAFsWL5dW2C2Vtsjt1puFHWTRTyw19KJmiSMPDXDmNjtI4fCtdRAfffbl7LXF9aaGhoeIAdTRw9VENhtuG+J71+WG73GxXaC62mqfS1lOSY5GgHtBBBB5EEEgg7ggkHkvhRAbjV6hXR9HUwW20WCyy1cToampttvbDNKxw2cwO3PA1w5EM4QRy7OS05EQGRlvNdJjtPYONjaGCpkqmsa3Yule1rS5x79gwAeG58SlVea6qsNFZZ3sfS0M0stPu3z2GTh428Xbw7sB27iSe8rHIgPbSTyUtXDUwkCSF7ZGEjcbg7hZ+xZtkVkzp2aWysbBd31ElQ94YCx7pCS8Fh5Fp3PJa2iA8pHF73Pd2uJJWaxXJ7hjwq4aeCirKKta1tXRVsAlgm4Tu0lp5hzSTs5pBG558ysGiAzmVZPX5C2jgngoqKioWubS0dFAIoYeI7uIHaXOIG7nEk7DnyCwaIgCIiAIiID//2Q==";

export default function App() {
  const [inputs, setInputs] = useState(DEFAULTS);
  const [activeTab, setActiveTab] = useState("inputs");
  const printRef = useRef(null);

  const set = (key) => (val) => setInputs((prev) => ({ ...prev, [key]: val }));
  const setStr = (key) => (e) =>
    setInputs((prev) => ({ ...prev, [key]: e.target.value }));

  const calc = calculate(inputs);

  const DISCLAIMER_TEXT = "This proforma analysis is based solely on information provided by the user and is offered by Traverse Home Loans for informational purposes only. Traverse Home Loans does not verify or guarantee the accuracy or completeness of any inputs, including but not limited to purchase price, rental income, expenses, interest rates, or appreciation assumptions. All projections and figures are estimates only and are subject to change without notice. This analysis does not constitute financial, legal, tax, or investment advice. Actual results may vary materially from the projections shown, and past performance is not indicative of future results. Real estate investing involves risk, and all investment decisions are made at the sole discretion of the user. Traverse Home Loans assumes no liability for any outcomes resulting from the use of this analysis. Traverse Home Loans is a licensed mortgage broker. This tool is intended solely to assist in evaluating potential financing scenarios and does not represent a loan commitment, pre-approval, or guarantee of financing. All loan approvals are subject to underwriting, creditworthiness, property appraisal, and lender-specific requirements. By downloading or using this report, you acknowledge and agree to the terms outlined in this disclaimer.";

  const [exporting, setExporting] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);    // gating state
  const [hasSubmitted, setHasSubmitted] = useState(false);  // rate limit — one submission per session
  const [pendingAction, setPendingAction] = useState(null); // "tab" | "pdf"
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "" });
  const [leadErrors, setLeadErrors] = useState({});
  const [consentChecked, setConsentChecked] = useState(false);

  // Gate handler — call this instead of directly switching tab or printing
  const requestAccess = (action) => {
    if (isUnlocked) {
      if (action === "tab") setActiveTab("results");
      if (action === "pdf") handlePrint();
    } else {
      setPendingAction(action);
      setShowLeadModal(true);
    }
  };

  // ── YOUR GoHighLevel Webhook URL ──────────────────────────────────────────
  // Replace this with your actual GHL webhook URL from:
  // GoHighLevel → Automations → Create Workflow → Webhook Trigger → Copy URL
  const GHL_WEBHOOK_URL = "/.netlify/functions/submit-lead"; // Direct Netlify function URL
  // ─────────────────────────────────────────────────────────────────────────

  const validateLead = () => {
    const errs = {};
    if (!leadForm.name.trim()) errs.name = "Name is required";
    if (!leadForm.email.trim()) errs.email = "Email is required";
    else if (!/^[^@]+@[^@]+\.[^@]+$/.test(leadForm.email)) errs.email = "Enter a valid email";
    if (!leadForm.phone.trim()) errs.phone = "Phone is required";
    else if (leadForm.phone.replace(/\D/g, "").length < 10) errs.phone = "Enter a valid phone number";
    if (!consentChecked) errs.consent = "You must agree to be contacted to continue";
    setLeadErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitLead = async () => {
    if (hasSubmitted) {
      // Already submitted this session — skip silently and just unlock
      setIsUnlocked(true);
      setLeadSubmitted(true);
      setTimeout(() => {
        setShowLeadModal(false);
        setLeadSubmitted(false);
        if (pendingAction === "tab") setActiveTab("results");
        if (pendingAction === "pdf") { setActiveTab("results"); setTimeout(() => handlePrint(), 300); }
        setPendingAction(null);
      }, 1500);
      return;
    }
    if (!validateLead()) return;
    setLeadSubmitting(true);
    const c = calculate(inputs);
    const y = c.years;
    const ti = c.totalPropertyInvestment;

    // ── Contact Info ─────────────────────────────────────────────────────────
    const payload = {
      // Standard GHL contact fields
      firstName:              leadForm.name.split(" ")[0] || leadForm.name,
      lastName:               leadForm.name.split(" ").slice(1).join(" ") || "",
      name:                   leadForm.name,
      email:                  leadForm.email,
      phone:                  "+1" + leadForm.phone.replace(/\D/g, ""),

      // ── Deal Inputs ────────────────────────────────────────────────────────
      property_address:       inputs.address || "Not provided",
      property_type:          inputs.propertyType === "sfr" ? "SFR" : inputs.propertyType === "duplex" ? "Duplex" : inputs.propertyType === "triplex" ? "Triplex" : "Fourplex",
      purchase_price:         inputs.purchasePrice,
      down_payment:           Math.round(c.downPayment),
      down_payment_pct:       (inputs.downPaymentPct * 100).toFixed(2) + "%",
      loan_amount:            Math.round(inputs.purchasePrice - c.downPayment),
      interest_rate:          (inputs.interestRate * 100).toFixed(3) + "%",
      closing_cost_pct:       (inputs.closingCostPct * 100).toFixed(2) + "%",
      rehab_expenses:         inputs.rehabExpenses,
      acquisitions_fee:       inputs.acquisitionsFee,
      total_investment:       Math.round(ti),
      rent:                   Math.round(c.monthlyRent),
      monthly_taxes:          inputs.monthlyTaxes,
      monthly_insurance:      inputs.monthlyInsurance,
      monthly_hoa:            inputs.monthlyHOA,
      property_mgmt_pct:      (inputs.propertyMgmtPct * 100).toFixed(2) + "%",
      vacancy_repairs_pct:    (inputs.vacancyRepairsPct * 100).toFixed(2) + "%",
      annual_rent_increase:   (inputs.annualRentIncrease * 100).toFixed(2) + "%",
      appreciation_yr1_2:     (inputs.appreciationYr12 * 100).toFixed(2) + "%",
      appreciation_yr3_5:     (inputs.appreciationYr35 * 100).toFixed(2) + "%",

      // ── Calculated Deal Metrics ────────────────────────────────────────────
      loan_type:              "30-Year Fixed",
      monthly_pi:             Math.round(c.monthlyPI),
      piti_monthly:           Math.round(c.pitiMonthly),
      dscr:                   c.dscrRatio.toFixed(2),
      dscr_status:            c.dscrRatio >= 1.25 ? "Strong" : c.dscrRatio >= 1.0 ? "Qualifies" : "Does Not Qualify",
      cap_rate:               (c.capRate * 100).toFixed(2) + "%",
      noi_yr1:                Math.round(y[0].noi),
      expenses:               Math.round(y[0].taxes + y[0].insurance + y[0].hoa + y[0].propMgmt + y[0].vacancy),

      // Cash flow
      cash_flow:              Math.round(y[0].monthlyCashFlow),
      cash_flow_yr1_monthly:  Math.round(y[0].monthlyCashFlow),
      cash_flow_yr3_monthly:  Math.round(y[2].monthlyCashFlow),
      cash_flow_yr5_monthly:  Math.round(y[4].monthlyCashFlow),

      // ROI
      coc_roi_yr1:            (y[0].cocROI * 100).toFixed(2) + "%",
      coc_roi_yr5:            (y[4].cocROI * 100).toFixed(2) + "%",
      coc_plus_pr_yr1:        (y[0].cocPlusPR * 100).toFixed(2) + "%",
      coc_plus_pr_yr5:        (y[4].cocPlusPR * 100).toFixed(2) + "%",
      total_roi_yr5:          (y[4].totalROI * 100).toFixed(2) + "%",

      // Appreciation & equity
      appreciation_yr5:       Math.round(y[4].appreciation),
      cumulative_equity_yr5:  Math.round(y[4].cumulativeEquity),
      proceeds_on_sale_yr5:   Math.round(y[4].proceeds),
      depreciation_annual:    Math.round(y[0].depreciation),

      // ── Meta ──────────────────────────────────────────────────────────────
      source:                 "Traverse Home Loans — Investment Proforma Tool",
      source_url:             "https://investor.traversehomeloans.com",
      timestamp:              new Date().toISOString(),
    };

    try {
      const res = await fetch(GHL_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-webhook-secret": "thl-proforma-2024",
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Submission failed: " + res.status);
    } catch (err) {
      console.warn("GHL webhook error:", err);
    }
    setLeadSubmitting(false);
    setIsUnlocked(true);
    setHasSubmitted(true); // lock — prevent re-submission this session
    setLeadSubmitted(true);
    setTimeout(() => {
      setShowLeadModal(false);
      setLeadSubmitted(false);
      if (pendingAction === "tab") setActiveTab("results");
      if (pendingAction === "pdf") { setActiveTab("results"); setTimeout(() => handlePrint(), 300); }
      setPendingAction(null);
    }, 2500);
  };

  const handlePrint = async () => {
    setExporting(true);
    try {
      // Load jsPDF
      await new Promise((resolve) => {
        if (window.jspdf) return resolve();
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
        s.onload = resolve;
        document.head.appendChild(s);
      });
      await new Promise((resolve) => {
        if (window.jspdf && window.jspdf.jsPDF.prototype.autoTable) return resolve();
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js";
        s.onload = resolve;
        document.head.appendChild(s);
      });

      const { jsPDF } = window.jspdf;
      const c = calculate(inputs);
      const y = c.years;
      const ti = c.totalPropertyInvestment;
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 40;
      const contentW = pageW - margin * 2;
      let curY = margin;

      const navy = [13, 31, 53];
      // gold color removed — using THL blue instead
      const lgray = [245, 242, 238];
      const white = [255, 255, 255];
      const muted = [90, 106, 126];

      // ── Header bar: black bg + THL blue accent + embedded logo
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, pageW, 70, "F");
      doc.setFillColor(44, 181, 232);
      doc.rect(0, 67, pageW, 3, "F");

      // Embed the logo image (base64)
      try {
        const logoData = document.querySelector('header img') || document.querySelector('.modal-logo');
        const logoSrc = logoData ? logoData.src : null;
        if (logoSrc) {
          doc.addImage(logoSrc, "PNG", margin, 10, 140, 50);
        }
      } catch(e) {}

      // Address top-right
      if (inputs.address) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.text(inputs.address, pageW - margin, 28, { align: "right" });
      }
      // Tagline bottom-right
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(92, 203, 245);
      doc.text("INVESTMENT PROPERTY PROFORMA", pageW - margin, 56, { align: "right" });

      curY = 84;

      const detailParts = [inputs.dwelling, inputs.yearBuilt, inputs.sqft, inputs.bedBath].filter(Boolean);
      if (detailParts.length) {
        doc.setFontSize(9);
        doc.setTextColor(...muted);
        doc.text(detailParts.join("  •  "), margin, curY);
        curY += 16;
      }

      const sectionTitle = (title, yPos) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(...navy);
        doc.text(title.toUpperCase(), margin, yPos);
        doc.setDrawColor(44, 181, 232);
        doc.setLineWidth(1.5);
        doc.line(margin, yPos + 3, margin + contentW, yPos + 3);
        return yPos + 16;
      };

      // Key metric boxes
      curY = sectionTitle("Key Metrics", curY);
      const boxW = (contentW - 12) / 4;
      const boxes = [
        { label: "Purchase Price", value: fmtDollar(inputs.purchasePrice) },
        { label: "Total Investment", value: fmtDollar(ti) },
        { label: "Monthly P&I", value: fmtDollar(Math.round(c.monthlyPI)) },
        { label: "Cap Rate", value: fmtPct(c.capRate) },
      ];
      boxes.forEach((b, i) => {
        const bx = margin + i * (boxW + 4);
        // Box background
        doc.setFillColor(0, 0, 0);
        doc.roundedRect(bx, curY, boxW, 50, 5, 5, "F");
        // THL blue top accent bar
        doc.setFillColor(44, 181, 232);
        doc.roundedRect(bx, curY, boxW, 5, 2, 2, "F");
        // Value in THL blue
        doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(44, 181, 232);
        doc.text(b.value, bx + boxW / 2, curY + 28, { align: "center" });
        // Label in light gray
        doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(180, 200, 220);
        doc.text(b.label.toUpperCase(), bx + boxW / 2, curY + 40, { align: "center" });
      });
      curY += 8; // extra padding after taller boxes
      curY += 56;

      // DSCR row
      const dscrColor = c.dscrRatio >= 1.0 ? [26, 107, 58] : [176, 48, 48];
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...muted);
      doc.text("DSCR Ratio:", margin, curY);
      doc.setFont("helvetica", "bold"); doc.setTextColor(...dscrColor);
      const dscrLabel = c.dscrRatio >= 1.25 ? "Strong" : c.dscrRatio >= 1.0 ? "Qualifies" : "Does Not Qualify";
      doc.text(c.dscrRatio.toFixed(2) + "  " + dscrLabel, margin + 62, curY);
      doc.setFont("helvetica", "normal"); doc.setTextColor(...muted);
      doc.text("PITI Monthly:", margin + 220, curY);
      doc.setFont("helvetica", "bold"); doc.setTextColor(...navy);
      doc.text(fmtDollar(Math.round(c.pitiMonthly)), margin + 295, curY);
      doc.setFont("helvetica", "normal"); doc.setTextColor(...muted);
      doc.text("Down Payment:", margin + 370, curY);
      doc.setFont("helvetica", "bold"); doc.setTextColor(...navy);
      doc.text(fmtDollar(c.downPayment), margin + 450, curY);
      curY += 22;

      // Income / Expenses table
      curY = sectionTitle("Income / Expenses (5-Year)", curY);
      doc.autoTable({
        startY: curY,
        margin: { left: margin, right: margin },
        head: [["Item", "Year 1", "Year 2", "Year 3", "Year 4", "Year 5"]],
        body: [
          ["Gross Rent",           ...y.map(yr => fmtDollar(Math.round(yr.grossRent)))],
          ["Taxes",                ...y.map(yr => fmtDollar(Math.round(yr.taxes)))],
          ["Insurance",            ...y.map(yr => fmtDollar(Math.round(yr.insurance)))],
          ["HOA",                  ...y.map(yr => fmtDollar(Math.round(yr.hoa)))],
          ["Property Management",  ...y.map(yr => fmtDollar(Math.round(yr.propMgmt)))],
          ["Vacancy / Repairs",    ...y.map(yr => fmtDollar(Math.round(yr.vacancy)))],
          ["Net Operating Income", ...y.map(yr => fmtDollar(Math.round(yr.noi)))],
          ["Annual P&I",           ...y.map(yr => fmtDollar(Math.round(yr.annualPI)))],
          ["Gross Profits",        ...y.map(yr => fmtDollar(Math.round(yr.grossProfit)))],
          ["Monthly Cash Flow",    ...y.map(yr => fmtDollar(Math.round(yr.monthlyCashFlow)))],
          ["Cash on Cash ROI",     ...y.map(yr => fmtPct(yr.cocROI))],
          ["CoC + Principal Reduction", ...y.map(yr => fmtPct(yr.cocPlusPR))],
        ],
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [0,0,0], textColor: white, fontStyle: "bold", fontSize: 8 },
        alternateRowStyles: { fillColor: lgray },
        columnStyles: { 0: { cellWidth: 130, fontStyle: "bold", textColor: muted } },
        didParseCell: (data) => {
          if (data.section !== "body") return;
          if ([6, 8, 9].includes(data.row.index)) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = navy;
          }
          if (data.row.index === 8 && data.column.index > 0) {
            const val = y[data.column.index - 1]?.grossProfit;
            data.cell.styles.textColor = val < 0 ? [176, 48, 48] : [26, 107, 58];
          }
        },
      });
      curY = doc.lastAutoTable.finalY + 16;

      // Appreciation table
      curY = sectionTitle("Annual Appreciation / ROI", curY);
      doc.autoTable({
        startY: curY,
        margin: { left: margin, right: margin },
        head: [["Item", "Year 1", "Year 2", "Year 3", "Year 4", "Year 5"]],
        body: [
          ["Principal Reduction",      ...y.map(yr => fmtDollar(Math.round(yr.principalPaid)))],
          ["Appreciation",             ...y.map(yr => fmtDollar(Math.round(yr.appreciation)))],
          ["Appreciation ROI",         ...y.map(yr => fmtPct(yr.appreciationROI))],
          ["Cumulative Equity Gain",   ...y.map(yr => fmtDollar(Math.round(yr.cumulativeEquity)))],
          ["Proceeds in Event of Sale",...y.map(yr => fmtDollar(Math.round(yr.proceeds)))],
          ["Deprec / Tax Savings",     ...y.map(yr => fmtDollar(Math.round(yr.depreciation)))],
          ["Depreciation ROI",         ...y.map(yr => fmtPct(yr.deprecROI))],
        ],
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [0,0,0], textColor: white, fontStyle: "bold", fontSize: 8 },
        alternateRowStyles: { fillColor: lgray },
        columnStyles: { 0: { cellWidth: 130, fontStyle: "bold", textColor: muted } },
        didParseCell: (data) => {
          if (data.section === "body" && [3, 4].includes(data.row.index)) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = navy;
          }
        },
      });
      curY = doc.lastAutoTable.finalY + 16;

      // ROI Summary table
      curY = sectionTitle("Return on Investment Summary", curY);
      doc.autoTable({
        startY: curY,
        margin: { left: margin, right: margin },
        head: [["Metric", "Yr 1-2 Avg", "Year 3", "Year 4", "Year 5"]],
        body: [
          ["Cash on Cash ROI",
            fmtPct((y[0].cocROI + y[1].cocROI) / 2), fmtPct(y[2].cocROI), fmtPct(y[3].cocROI), fmtPct(y[4].cocROI)],
          ["CoC + Principal Reduction",
            fmtPct((y[0].cocPlusPR + y[1].cocPlusPR) / 2), fmtPct(y[2].cocPlusPR), fmtPct(y[3].cocPlusPR), fmtPct(y[4].cocPlusPR)],
          ["CoC + PR + Depreciation",
            fmtPct(((y[0].grossProfit+y[0].annualPrincipalPaid+y[0].depreciation+y[1].grossProfit+y[1].annualPrincipalPaid+y[1].depreciation)/2)/ti),
            fmtPct((y[2].grossProfit+y[2].annualPrincipalPaid+y[2].depreciation)/ti),
            fmtPct((y[3].grossProfit+y[3].annualPrincipalPaid+y[3].depreciation)/ti),
            fmtPct((y[4].grossProfit+y[4].annualPrincipalPaid+y[4].depreciation)/ti)],
          ["CoC + PR + Depreciation + Appreciation",
            fmtPct((y[0].totalROI + y[1].totalROI) / 2), fmtPct(y[2].totalROI), fmtPct(y[3].totalROI), fmtPct(y[4].totalROI)],
        ],
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [0,0,0], textColor: white, fontStyle: "bold", fontSize: 8 },
        alternateRowStyles: { fillColor: lgray },
        columnStyles: { 0: { cellWidth: 180, fontStyle: "bold", textColor: muted } },
      });

      // Disclaimer — rendered via autoTable for reliable full-width wrapping
      const disclaimerText = "This proforma analysis is based solely on information provided by the user and is offered by Traverse Home Loans for informational purposes only. Traverse Home Loans does not verify or guarantee the accuracy or completeness of any inputs, including but not limited to purchase price, rental income, expenses, interest rates, or appreciation assumptions. All projections and figures are estimates only and are subject to change without notice. This analysis does not constitute financial, legal, tax, or investment advice. Actual results may vary materially from the projections shown, and past performance is not indicative of future results. Real estate investing involves risk, and all investment decisions are made at the sole discretion of the user. Traverse Home Loans assumes no liability for any outcomes resulting from the use of this analysis. Traverse Home Loans is a licensed mortgage broker. This tool is intended solely to assist in evaluating potential financing scenarios and does not represent a loan commitment, pre-approval, or guarantee of financing. All loan approvals are subject to underwriting, creditworthiness, property appraisal, and lender-specific requirements. By downloading or using this report, you acknowledge and agree to the terms outlined in this disclaimer.";
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        tableWidth: pageW - margin * 2,
        margin: { left: margin, right: margin },
        head: [["DISCLAIMER"]],
        body: [[disclaimerText]],
        headStyles: {
          fillColor: [44, 181, 232], textColor: [255, 255, 255],
          fontStyle: "bold", fontSize: 7, cellPadding: { top: 5, bottom: 5, left: 6, right: 6 }
        },
        bodyStyles: {
          fontSize: 6.5, textColor: [90, 106, 126], lineHeight: 1.5,
          fillColor: [245, 242, 238],
          cellPadding: { top: 8, bottom: 8, left: 6, right: 6 }
        },
      });
      curY = doc.lastAutoTable.finalY + 10;

      // Footer on each page
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const ph = doc.internal.pageSize.getHeight();
        // Footer bar — taller to fit two lines
        doc.setFillColor(0, 0, 0);
        doc.rect(0, ph - 38, pageW, 38, "F");
        doc.setFillColor(44, 181, 232);
        doc.rect(0, ph - 38, pageW, 2, "F");
        // Line 1: company name + equal housing
        doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(255, 255, 255);
        doc.text("Traverse Home Loans", margin, ph - 24);
        doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(180, 200, 220);
        doc.text("Equal Housing Opportunity  |  Co. NMLS #2560345  |  LO NMLS #1519991  |  AZ MB-2014481  |  AZ LO-2014493", pageW / 2, ph - 24, { align: "center" });
        doc.text("Page " + i + " of " + totalPages, pageW - margin, ph - 24, { align: "right" });
        // Line 2: confidential note
        doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(120, 150, 175);
        doc.text("This report is confidential and intended solely for the recipient. All figures are projections based on user-provided inputs.", margin, ph - 11);
      }

      const filename = inputs.address ? "Proforma - " + inputs.address + ".pdf" : "Investment_Proforma.pdf";
      doc.save(filename);
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("PDF export failed: " + err.message);
    }
    setExporting(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Source+Sans+3:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --navy: #0a0a0a;
          --navy-mid: #1a1a1a;
          --navy-light: #2a2a2a;
          --gold: #2cb5e8;
          --gold-light: #5ccbf5;
          --cream: #f4f6f8;
          --off-white: #eef2f5;
          --text: #0a0a0a;
          --text-muted: #5a6a7e;
          --border: #cdd5de;
          --positive: #1a6b3a;
          --negative: #b03030;
          --green-bg: #edf7f2;
          --red-bg: #fdf0f0;
          --shadow: 0 2px 16px rgba(0,0,0,0.10);
          --shadow-lg: 0 8px 40px rgba(0,0,0,0.18);
        }

        body {
          font-family: 'Source Sans 3', sans-serif;
          background: var(--cream);
          color: var(--text);
          min-height: 100vh;
        }

        /* ── Header ── */
        .header {
          background: #000000;
          padding: 0 40px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 72px;
          border-bottom: 3px solid var(--gold);
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .header-brand {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .header-icon {
          width: 38px; height: 38px;
          background: var(--gold);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px;
        }
        .header-title {
          font-family: 'Playfair Display', serif;
          font-size: 18px;
          color: white;
          letter-spacing: 0.3px;
        }
        .header-subtitle {
          font-size: 11px;
          color: var(--gold-light);
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }
        .header-actions {
          display: flex; gap: 10px; align-items: center;
        }
        .btn {
          padding: 8px 20px;
          border-radius: 6px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          border: none;
          letter-spacing: 0.5px;
          transition: all 0.15s;
        }
        .btn-primary {
          background: var(--gold);
          color: #0a0a0a;
          font-weight: 700;
        }
        .btn-primary:hover { background: var(--gold-light); }
        .btn-outline {
          background: transparent;
          color: white;
          border: 1.5px solid rgba(255,255,255,0.3);
        }
        .btn-outline:hover { border-color: var(--gold); color: var(--gold-light); }

        /* ── Tabs ── */
        .tabs {
          background: var(--navy-mid);
          padding: 0 40px;
          display: flex;
          gap: 2px;
        }
        .tab {
          padding: 14px 22px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.55);
          cursor: pointer;
          border-bottom: 3px solid transparent;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          transition: all 0.15s;
          background: none;
          border-left: none; border-right: none; border-top: none;
        }
        .tab:hover { color: rgba(255,255,255,0.85); }
        .tab--active {
          color: #fff;
          border-bottom-color: var(--gold);
        }

        /* ── Layout ── */
        .page { max-width: 1200px; margin: 0 auto; padding: 36px 32px 48px; }
        /* ── Site Footer ── */
        .site-footer {
          background: #000;
          border-top: 3px solid var(--gold);
          padding: 24px 40px 20px;
        }
        .site-footer-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 14px;
          padding-bottom: 14px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .site-footer-brand {
          font-size: 13px;
          font-weight: 700;
          color: #fff;
          letter-spacing: 0.5px;
        }
        .site-footer-nmls {
          display: flex;
          align-items: center;
          gap: 18px;
          flex-wrap: wrap;
        }
        .site-footer-nmls span {
          font-size: 11px;
          color: rgba(255,255,255,0.55);
        }
        .site-footer-nmls strong { color: var(--gold); font-weight: 600; }
        .footer-eq-housing {
          display: flex;
          align-items: center;
          gap: 7px;
          font-size: 11px;
          color: rgba(255,255,255,0.55);
        }
        .footer-eq-icon {
          width: 20px; height: 20px;
          border: 2px solid var(--gold);
          border-radius: 2px;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; color: var(--gold); font-weight: 900;
          flex-shrink: 0; line-height: 1;
        }
        .site-footer-disclaimer {
          font-size: 9.5px;
          color: rgba(255,255,255,0.3);
          line-height: 1.65;
        }
        @media (max-width: 600px) {
          .site-footer { padding: 20px 16px 16px; }
          .site-footer-top { flex-direction: column; align-items: flex-start; }
        }

        /* ── Address banner ── */
        .address-banner {
          background: white;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 20px 24px;
          margin-bottom: 28px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow: var(--shadow);
        }
        .address-icon {
          font-size: 24px;
          flex-shrink: 0;
        }
        .address-input-group { flex: 1; }
        .address-input {
          width: 100%;
          font-family: 'Playfair Display', serif;
          font-size: 19px;
          font-weight: 600;
          color: var(--navy);
          border: none;
          outline: none;
          background: transparent;
        }
        .address-input::placeholder { color: var(--text-muted); font-weight: 600; }
        .address-meta {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          margin-top: 8px;
        }
        .address-meta-input {
          font-size: 13px;
          color: var(--text-muted);
          border: none;
          outline: none;
          background: transparent;
          padding: 0;
          width: 100%;
          min-width: 0;
        }
        .address-meta-input::placeholder { color: #aab; }

        /* ── Grid layouts ── */
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
        .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; }
        .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }

        /* ── Cards ── */
        .card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 24px;
          box-shadow: var(--shadow);
        }
        .card-title {
          font-family: 'Playfair Display', serif;
          font-size: 14px;
          font-weight: 700;
          color: var(--navy);
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 2px solid var(--gold);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .card-title-icon { font-size: 16px; }
        .mb-6 { margin-bottom: 24px; }
        .mb-4 { margin-bottom: 16px; }

        /* ── Field ── */
        .field-wrapper { margin-bottom: 14px; }
        .field-label {
          display: block;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-bottom: 5px;
        }
        .field-hint {
          display: block;
          font-size: 10px;
          color: #9aabb0;
          margin-bottom: 3px;
        }
        .field-input {
          width: 100%;
          padding: 9px 12px;
          border: 1.5px solid var(--border);
          border-radius: 6px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 14px;
          color: var(--text);
          background: var(--off-white);
          transition: border-color 0.15s, background 0.15s;
          outline: none;
        }
        .field-input:focus {
          border-color: var(--navy-light);
          background: white;
        }

        /* ── Section header ── */
        .section-header { margin-bottom: 20px; }
        .section-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          color: var(--navy);
          font-weight: 700;
        }
        .section-subtitle {
          font-size: 13px;
          color: var(--text-muted);
          margin-top: 4px;
        }

        /* ── Metric cards ── */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 14px;
          margin-bottom: 28px;
        }
        .metric-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 18px 16px;
          text-align: center;
          box-shadow: var(--shadow);
        }
        .metric-card--highlight {
          background: var(--navy);
          border-color: var(--gold);
          border-width: 2px;
        }
        .metric-card--highlight .metric-value { color: var(--gold); }
        .metric-card--highlight .metric-label { color: rgba(255,255,255,0.65); }
        .metric-value {
          font-family: 'Playfair Display', serif;
          font-size: 22px;
          font-weight: 700;
          color: var(--navy);
          line-height: 1.1;
        }
        .metric-label {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.7px;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-top: 5px;
        }
        .metric-sub {
          font-size: 11px;
          color: var(--text-muted);
          margin-top: 3px;
        }

        /* ── DSCR badge ── */
        .dscr-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 600;
        }
        .dscr-pass { background: var(--green-bg); color: var(--positive); }
        .dscr-fail { background: var(--red-bg); color: var(--negative); }

        /* ── Summary highlights row ── */
        .highlight-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 28px;
        }
        .highlight-box {
          border-radius: 10px;
          padding: 20px 22px;
          border: 1px solid;
        }
        .highlight-box--blue {
          background: #eef4fb;
          border-color: #b8d0ea;
        }
        .highlight-box--green {
          background: var(--green-bg);
          border-color: #b0d9c0;
        }
        .highlight-box--gold {
          background: #eaf7fd;
          border-color: #93daf5;
        }
        .highlight-box-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: var(--text-muted);
          margin-bottom: 4px;
        }
        .highlight-box-value {
          font-family: 'Playfair Display', serif;
          font-size: 26px;
          font-weight: 700;
          color: var(--navy);
        }
        .highlight-box-sub {
          font-size: 12px;
          color: var(--text-muted);
          margin-top: 3px;
        }
        .highlight-neg { color: var(--negative) !important; }
        .highlight-pos { color: var(--positive) !important; }

        /* ── Year tables ── */
        .year-table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .year-table {
          width: 100%;
          min-width: 500px;
          border-collapse: collapse;
          font-size: 13px;
        }
        .year-table th {
          background: var(--navy);
          color: white;
          padding: 10px 14px;
          text-align: right;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .year-table-label-col { text-align: left !important; width: 220px; }
        .year-table-year-col { min-width: 110px; }
        .year-row td { padding: 8px 14px; border-bottom: 1px solid #ede8df; }
        .year-row--even td { background: var(--off-white); }
        .year-row--bold td { font-weight: 700; color: var(--navy); }
        .year-row--highlight td {
          background: #eef4fb !important;
          font-weight: 700;
        }
        .year-row-label { text-align: left; color: var(--text-muted); font-size: 12.5px; }
        .year-row-value { text-align: right; font-variant-numeric: tabular-nums; }
        .year-row-value--neg { color: var(--negative); }

        /* ── ROI Summary ── */
        .roi-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
          gap: 0;
          border: 1px solid var(--border);
          border-radius: 10px;
          overflow: hidden;
        }
        .roi-cell {
          padding: 12px 16px;
          font-size: 13px;
          border-bottom: 1px solid var(--border);
          border-right: 1px solid var(--border);
        }
        .roi-cell:last-child { border-right: none; }
        .roi-header {
          background: var(--navy);
          color: rgba(255,255,255,0.75);
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          text-align: center;
        }
        .roi-header:first-child { text-align: left; }
        .roi-label { color: var(--text-muted); font-size: 12px; }
        .roi-value { text-align: center; font-weight: 600; font-variant-numeric: tabular-nums; }
        .roi-row-alt .roi-cell { background: var(--off-white); }

        /* ── Cash flow bar chart ── */
        .cashflow-chart {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          height: 120px;
          padding: 0 4px;
        }
        .cf-bar-group {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          height: 100%;
          justify-content: flex-end;
        }
        .cf-bar-label-top {
          font-size: 11px;
          font-weight: 700;
          white-space: nowrap;
        }
        .cf-bar-pos { color: var(--positive); }
        .cf-bar-neg { color: var(--negative); }
        .cf-bar {
          width: 100%;
          border-radius: 4px 4px 0 0;
          min-height: 4px;
          transition: height 0.3s;
        }
        .cf-bar--pos { background: linear-gradient(180deg, #4cae76, #1a6b3a); }
        .cf-bar--neg { background: linear-gradient(180deg, #e06060, #b03030); }
        .cf-bar-year {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.5px;
          color: var(--text-muted);
          text-transform: uppercase;
        }

        /* ── Print styles ── */
        @media print {
          body { background: white !important; }
          .header { position: static; }
          .tabs { display: none; }
          .no-print { display: none !important; }
          .page { padding: 20px; }
          .print-only { display: block !important; }
          .card { break-inside: avoid; box-shadow: none; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }

        /* ── Property Type Selector ── */
        .prop-type-selector {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
          margin-bottom: 20px;
        }
        .prop-type-btn {
          padding: 10px 6px;
          border: 1.5px solid var(--border);
          border-radius: 8px;
          background: var(--off-white);
          cursor: pointer;
          text-align: center;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 11px;
          font-weight: 600;
          color: var(--text-muted);
          letter-spacing: 0.3px;
          transition: all 0.15s;
        }
        .prop-type-btn:hover { border-color: var(--gold); color: var(--text); }
        .prop-type-btn.active {
          background: var(--navy);
          border-color: var(--navy);
          color: white;
        }
        .prop-type-btn .pt-badge {
          width: 28px; height: 28px;
          border-radius: 50%;
          background: var(--off-white);
          border: 1.5px solid var(--border);
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 5px;
          font-size: 13px; font-weight: 700;
          color: var(--text);
          transition: background 0.15s, border-color 0.15s, color 0.15s;
        }
        .prop-type-btn.active .pt-badge {
          background: var(--gold);
          border-color: var(--gold);
          color: #000;
        }
        .unit-rents-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
        }
        .unit-rent-total {
          background: var(--off-white);
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 10px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 4px;
          margin-bottom: 16px;
        }
        .unit-rent-total-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-muted);
        }
        .unit-rent-total-value {
          font-size: 15px;
          font-weight: 700;
          color: var(--navy);
        }
        @media (max-width: 480px) {
          .prop-type-selector { grid-template-columns: repeat(2, 1fr); }
          .unit-rents-grid { grid-template-columns: 1fr; }
        }
        /* ── Lead Capture Modal ── */
        .modal-overlay {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(0,0,0,0.65);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 20px;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 460px;
          overflow: hidden;
          box-shadow: 0 24px 80px rgba(0,0,0,0.35);
          animation: slideUp 0.25s ease;
        }
        @keyframes slideUp { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .modal-header {
          background: #000;
          padding: 24px 28px 20px;
          border-bottom: 3px solid var(--gold);
          position: relative;
        }
        .modal-logo { height: 44px; object-fit: contain; }
        .modal-header-sub {
          font-size: 11px; font-weight: 700; letter-spacing: 2px;
          text-transform: uppercase; color: rgba(255,255,255,0.6);
          margin-top: 6px;
        }
        .modal-close {
          position: absolute; top: 16px; right: 16px;
          background: rgba(255,255,255,0.1); border: none;
          color: white; border-radius: 50%; width: 30px; height: 30px;
          font-size: 16px; cursor: pointer; display: flex;
          align-items: center; justify-content: center;
          transition: background 0.15s;
        }
        .modal-close:hover { background: rgba(255,255,255,0.2); }
        .modal-body { padding: 28px; }
        .modal-title {
          font-family: 'Playfair Display', serif;
          font-size: 20px; font-weight: 700;
          color: var(--navy); margin-bottom: 6px;
        }
        .modal-desc {
          font-size: 13px; color: var(--text-muted);
          margin-bottom: 22px; line-height: 1.5;
        }
        .modal-property-pill {
          display: inline-flex; align-items: center; gap: 6px;
          background: var(--off-white); border: 1px solid var(--border);
          border-radius: 20px; padding: 5px 12px;
          font-size: 12px; color: var(--text-muted);
          margin-bottom: 22px;
        }
        .modal-field { margin-bottom: 16px; }
        .modal-field label {
          display: block; font-size: 11px; font-weight: 700;
          letter-spacing: 0.8px; text-transform: uppercase;
          color: var(--text-muted); margin-bottom: 5px;
        }
        .modal-field input {
          width: 100%; padding: 11px 14px;
          border: 1.5px solid var(--border); border-radius: 8px;
          font-family: 'Source Sans 3', sans-serif; font-size: 14px;
          color: var(--text); background: var(--off-white);
          outline: none; transition: border-color 0.15s, background 0.15s;
        }
        .modal-field input:focus { border-color: var(--gold); background: white; }
        .modal-field input.error { border-color: var(--negative); }
        .modal-error {
          font-size: 11px; color: var(--negative);
          margin-top: 4px; display: block;
        }
        .modal-submit {
          width: 100%; padding: 13px;
          background: var(--gold); color: #000;
          border: none; border-radius: 8px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 14px; font-weight: 700;
          letter-spacing: 0.5px; cursor: pointer;
          margin-top: 6px; transition: background 0.15s;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .modal-submit:hover:not(:disabled) { background: var(--gold-light); }
        .modal-submit:disabled { opacity: 0.6; cursor: not-allowed; }
        .modal-consent {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 12px;
          cursor: pointer;
        }
        .modal-consent input[type="checkbox"] {
          width: 18px;
          height: 18px;
          min-width: 18px;
          margin-top: 2px;
          accent-color: var(--gold);
          cursor: pointer;
        }
        .modal-consent-text {
          font-size: 10.5px;
          color: var(--text-muted);
          line-height: 1.5;
        }
        .modal-consent-error {
          font-size: 11px;
          color: var(--negative);
          margin-bottom: 8px;
          display: block;
        }
        .modal-disclaimer {
          font-size: 10.5px; color: var(--text-muted);
          text-align: center; margin-top: 12px; line-height: 1.4;
        }
        /* Thank you state */
        .modal-thankyou {
          padding: 40px 28px;
          text-align: center;
        }
        .modal-thankyou-icon {
          width: 64px; height: 64px; border-radius: 50%;
          background: var(--green-bg); border: 2px solid #b0d9c0;
          display: flex; align-items: center; justify-content: center;
          font-size: 28px; margin: 0 auto 16px;
        }
        .modal-thankyou-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px; font-weight: 700;
          color: var(--navy); margin-bottom: 8px;
        }
        .modal-thankyou-sub {
          font-size: 13px; color: var(--text-muted); line-height: 1.5;
        }
        .modal-progress {
          width: 100%; height: 4px;
          background: var(--border); border-radius: 4px;
          margin-top: 24px; overflow: hidden;
        }
        .modal-progress-bar {
          height: 100%; background: var(--gold);
          border-radius: 4px;
          animation: progress 2.5s linear forwards;
        }
        @keyframes progress { from { width: 0%; } to { width: 100%; } }
        .print-only { display: none; }
        .disclaimer-box {
          margin-top: 32px;
          padding: 16px 20px;
          background: var(--off-white);
          border: 1px solid var(--border);
          border-left: 3px solid var(--gold);
          border-radius: 6px;
        }
        .disclaimer-box p {
          font-size: 10px;
          color: var(--text-muted);
          line-height: 1.6;
          margin: 0;
        }
        .disclaimer-box strong {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: var(--text);
          display: block;
          margin-bottom: 6px;
        }

        /* ── Tablet ── */
        @media (max-width: 800px) {
          .header { padding: 0 16px; height: auto; min-height: 64px; flex-wrap: wrap; gap: 8px; padding-top: 10px; padding-bottom: 10px; }
          .header-brand img { height: 44px; }
          .page { padding: 20px 14px 60px; }
          .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr; }
          .highlight-row { grid-template-columns: 1fr 1fr; }
          .metrics-grid { grid-template-columns: repeat(2, 1fr); }
          .tabs { padding: 0 14px; }
          .tab { padding: 12px 12px; font-size: 11px; }
          .site-footer { padding: 20px 16px 16px; }
          .site-footer-top { flex-direction: column; align-items: flex-start; gap: 10px; }
        }

        /* ── Mobile ── */
        @media (max-width: 480px) {
          .header {
            padding: 10px 14px;
            height: auto;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
          }
          .header-brand { gap: 8px; }
          .header-brand img { height: 36px; }
          .btn-outline { padding: 7px 12px; font-size: 11px; }
          .tabs { padding: 0; gap: 0; }
          .tab {
            flex: 1;
            padding: 11px 6px;
            font-size: 10.5px;
            text-align: center;
            border-radius: 0;
          }
          .page { padding: 14px 12px 40px; }
          .address-banner { flex-direction: column; align-items: flex-start; gap: 6px; padding: 14px; }
          .address-icon { display: none; }
          .address-input-group { width: 100%; }
          .address-input { font-size: 15px; }
          .address-meta { grid-template-columns: repeat(2, 1fr); gap: 6px; margin-top: 8px; }
          .address-meta-input { font-size: 12px; }
          .card { padding: 16px 14px; }
          .card-title { font-size: 12px; }
          .grid-2, .grid-3, .grid-4,
          .highlight-row { grid-template-columns: 1fr; }
          .metrics-grid { grid-template-columns: repeat(2, 1fr); gap: 8px; }
          .metric-value { font-size: 17px; }
          .metric-label { font-size: 9px; }
          .metric-card { padding: 12px 10px; }
          .highlight-box { padding: 12px; }
          .highlight-value { font-size: 22px; }
          .highlight-label { font-size: 10px; }
          .bar-chart { gap: 6px; }
          .bar-label { font-size: 9px; }
          .bar-value { font-size: 9px; }
          .year-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; margin: 0 -12px; padding: 0 12px; }
          .year-table { min-width: 520px; }
          .year-header, .year-row { font-size: 10px; }
          .year-cell { padding: 7px 6px; }
          .section-title { font-size: 13px; }
          .btn { padding: 10px 18px; font-size: 12px; }
          .btn-primary { width: 100%; }
          .dscr-row { flex-direction: column; gap: 8px; align-items: flex-start; }
          .site-footer { padding: 16px 14px 14px; }
          .site-footer-top { flex-direction: column; align-items: flex-start; gap: 10px; }
          .site-footer-nmls { flex-direction: column; gap: 6px; align-items: flex-start; }
          .site-footer-brand { font-size: 12px; }
          .modal { margin: 0 8px; border-radius: 12px; }
          .modal-body { padding: 20px 16px; }
          .modal-header { padding: 18px 16px 14px; }
          .modal-title { font-size: 17px; }
          .modal-submit { padding: 12px; }
        }
      `}</style>

      {/* Header */}
      <header className="header no-print">
        <div className="header-brand">
          <img src="data:image/png;base64,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" alt="Traverse Home Loans" style={{height: "56px", objectFit: "contain"}} />
          <div style={{marginLeft: "14px", borderLeft: "1px solid rgba(255,255,255,0.15)", paddingLeft: "14px"}}>
            <div style={{color: "#ffffff", fontFamily: "Source Sans 3, sans-serif", fontSize: "14px", fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase"}}>INVESTMENT PROPERTY PROFORMA</div>
          </div>
        </div>

      </header>

      {/* Tabs */}
      <div className="tabs no-print">
        {["inputs", "results"].map((t) => (
          <button
            key={t}
            className={cls("tab", activeTab === t && "tab--active")}
            onClick={() => t === "results" ? requestAccess("tab") : setActiveTab(t)}
          >
            {t === "inputs" ? "📋 Deal Inputs" : "📊 Analysis & Results" + (isUnlocked ? "" : " 🔒")}
          </button>
        ))}
      </div>

      <div className="page" ref={printRef}>
        {/* Address Banner — always visible */}
        <div className="address-banner">
          <div className="address-icon">📍</div>
          <div className="address-input-group">
            <input
              className="address-input"
              placeholder="Enter property address..."
              value={inputs.address}
              onChange={setStr("address")}
            />
            <div className="address-meta">
              <input className="address-meta-input" placeholder="Dwelling type" value={inputs.dwelling} onChange={setStr("dwelling")} />
              <input className="address-meta-input" placeholder="Year built" value={inputs.yearBuilt} onChange={setStr("yearBuilt")} />
              <input className="address-meta-input" placeholder="Sq. ft." value={inputs.sqft} onChange={setStr("sqft")} />
              <input className="address-meta-input" placeholder="Bed / Bath" value={inputs.bedBath} onChange={setStr("bedBath")} />
            </div>
          </div>
        </div>

        {/* ═══════════ INPUTS TAB ═══════════ */}
        {(activeTab === "inputs" || true) && (
          <div style={{ display: activeTab === "inputs" ? "block" : "none" }}>
            <div className="grid-2 mb-6">
              {/* Property Information */}
              <div className="card">
                <div className="card-title">
                  <span className="card-title-icon">🏡</span> Property Information
                </div>

                {/* Property Type Selector */}
                <div style={{marginBottom: "4px"}}>
                  <div style={{fontSize:"11px", fontWeight:"700", textTransform:"uppercase", letterSpacing:"0.5px", color:"var(--text-muted)", marginBottom:"8px"}}>Property Type &nbsp;/&nbsp; Unit Count</div>
                  <div className="prop-type-selector">
                    {[
                      { key: "sfr",      num: "1", label: "SFR" },
                      { key: "duplex",   num: "2", label: "Duplex" },
                      { key: "triplex",  num: "3", label: "Triplex" },
                      { key: "fourplex", num: "4", label: "Fourplex" },
                    ].map(pt => (
                      <button
                        key={pt.key}
                        className={"prop-type-btn" + (inputs.propertyType === pt.key ? " active" : "")}
                        onClick={() => set("propertyType")(pt.key)}
                      >
                        <span className="pt-badge">{pt.num}</span>
                        {pt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <Field label="Purchase Price" value={inputs.purchasePrice} onChange={set("purchasePrice")} />

                {/* Rent: single field for SFR, per-unit for multifamily */}
                {inputs.propertyType === "sfr" ? (
                  <Field label="Monthly Rent" value={inputs.monthlyRent} onChange={set("monthlyRent")} />
                ) : (
                  <div>
                    <div style={{fontSize:"11px", fontWeight:"700", textTransform:"uppercase", letterSpacing:"0.5px", color:"var(--text-muted)", margin:"16px 0 8px"}}>Monthly Rent Per Unit</div>
                    <div className="unit-rents-grid">
                      {Array.from({length: inputs.propertyType === "duplex" ? 2 : inputs.propertyType === "triplex" ? 3 : 4}, (_, i) => (
                        <Field
                          key={i}
                          label={"Unit " + (i + 1)}
                          value={inputs.unitRents[i] || 0}
                          onChange={(val) => {
                            const updated = [...inputs.unitRents];
                            updated[i] = val;
                            set("unitRents")(updated);
                          }}
                        />
                      ))}
                    </div>
                    <div className="unit-rent-total">
                      <span className="unit-rent-total-label">Total Monthly Rent</span>
                      <span className="unit-rent-total-value">
                        {fmtDollar(inputs.unitRents.slice(0, inputs.propertyType === "duplex" ? 2 : inputs.propertyType === "triplex" ? 3 : 4).reduce((s,r) => s + (Number(r)||0), 0))}
                      </span>
                    </div>
                  </div>
                )}

                <Field label="Monthly Taxes" value={inputs.monthlyTaxes} onChange={set("monthlyTaxes")} />
                <Field label="Monthly Insurance" value={inputs.monthlyInsurance} onChange={set("monthlyInsurance")} />
                <Field label="Monthly HOA" value={inputs.monthlyHOA} onChange={set("monthlyHOA")} />
                <Field label="Rehab Expenses" value={inputs.rehabExpenses} onChange={set("rehabExpenses")} />
                <Field label="Acquisitions Fee" value={inputs.acquisitionsFee} onChange={set("acquisitionsFee")} />
              </div>

              {/* Assumptions */}
              <div className="card">
                <div className="card-title">
                  <span className="card-title-icon">⚙️</span> Loan & Assumptions
                </div>
                <Field label="Down Payment %" value={inputs.downPaymentPct} onChange={set("downPaymentPct")} type="percent" />
                <Field label="Interest Rate" value={inputs.interestRate} onChange={set("interestRate")} type="percent" />
                <Field label="Closing Costs %" value={inputs.closingCostPct} onChange={set("closingCostPct")} type="percent" />
                <Field label="Property Management Fee %" value={inputs.propertyMgmtPct} onChange={set("propertyMgmtPct")} type="percent" />
                <Field label="Vacancy / Repairs %" value={inputs.vacancyRepairsPct} onChange={set("vacancyRepairsPct")} type="percent" />
                <Field label="Annual Rent Increase %" value={inputs.annualRentIncrease} onChange={set("annualRentIncrease")} type="percent" />
                <Field label="Annual Appreciation (Yr 1–2) %" value={inputs.appreciationYr12} onChange={set("appreciationYr12")} type="percent" />
                <Field label="Annual Appreciation (Yr 3–5) %" value={inputs.appreciationYr35} onChange={set("appreciationYr35")} type="percent" />
                <Field label="Closing Costs on Future Sale %" value={inputs.closingCostsOnSale} onChange={set("closingCostsOnSale")} type="percent" />
              </div>
            </div>

            {/* Calculated summary quick-view */}
            <div className="card mb-6">
              <div className="card-title">
                <span className="card-title-icon">💡</span> Quick Snapshot
              </div>
              <div className="metrics-grid">
                <MetricCard label="Purchase Price" value={fmtDollar(inputs.purchasePrice)} />
                <MetricCard label="Down Payment" value={fmtDollar(calc.downPayment)} sub={`${(inputs.downPaymentPct * 100).toFixed(0)}%`} />
                <MetricCard label="Closing Costs" value={fmtDollar(calc.estimatedClosingCosts)} />
                <MetricCard label="Total Investment" value={fmtDollar(calc.totalPropertyInvestment)} highlight />
                <MetricCard label="Loan Amount" value={fmtDollar(calc.loanAmount)} />
                <MetricCard label="Monthly P&I" value={fmtDollar(Math.round(calc.monthlyPI))} />
                <MetricCard label="PITI Monthly" value={fmtDollar(Math.round(calc.pitiMonthly))} />
                <MetricCard label="Cap Rate" value={fmtPct(calc.capRate)} highlight />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.5px", textTransform: "uppercase" }}>DSCR Ratio:</span>
                <span className={cls("dscr-badge", calc.dscrRatio >= 1.0 ? "dscr-pass" : "dscr-fail")}>
                  {calc.dscrRatio >= 1.0 ? "✓" : "⚠"} {calc.dscrRatio.toFixed(2)}
                  <span style={{ fontWeight: 400, fontSize: 12 }}>{calc.dscrRatio >= 1.25 ? " — Strong" : calc.dscrRatio >= 1.0 ? " — Qualifies" : " — Does Not Qualify"}</span>
                </span>
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <button className="btn btn-primary no-print" style={{ padding: "12px 36px", fontSize: 15 }} onClick={() => requestAccess("tab")}>
                View Full Analysis →
              </button>
            </div>
          </div>
        )}

        {/* ═══════════ RESULTS TAB ═══════════ */}
        {(activeTab === "results" || true) && (
          <div style={{ display: activeTab === "results" ? "block" : "none" }}>
            <SectionHeader
              title="Investment Summary"
              subtitle="5-year proforma analysis based on your inputs"
            />

            {/* Key metrics highlight row */}
            <div className="highlight-row mb-6">
              <div className="highlight-box highlight-box--blue">
                <div className="highlight-box-label">Total Investment Required</div>
                <div className="highlight-box-value">{fmtDollar(calc.totalPropertyInvestment)}</div>
                <div className="highlight-box-sub">{fmtDollar(calc.downPayment)} down + {fmtDollar(calc.estimatedClosingCosts)} closing</div>
              </div>
              <div className="highlight-box highlight-box--green">
                <div className="highlight-box-label">Year 1 Monthly Cash Flow</div>
                <div className={cls("highlight-box-value", calc.years[0]?.monthlyCashFlow < 0 ? "highlight-neg" : "highlight-pos")}>
                  {fmtDollar(Math.round(calc.years[0]?.monthlyCashFlow ?? 0))}
                </div>
                <div className="highlight-box-sub">Cap Rate: {fmtPct(calc.capRate)}</div>
              </div>
              <div className="highlight-box highlight-box--gold">
                <div className="highlight-box-label">Year 5 Proceeds on Sale</div>
                <div className="highlight-box-value">{fmtDollar(Math.round(calc.years[4]?.proceeds ?? 0))}</div>
                <div className="highlight-box-sub">After {(inputs.closingCostsOnSale * 100).toFixed(0)}% selling costs & loan payoff</div>
              </div>
            </div>

            {/* Financed Income Breakdown */}
            <div className="card mb-6">
              <div className="card-title"><span className="card-title-icon">💰</span> Financed Income Breakdown</div>
              <div className="grid-4" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
                <MetricCard label="Monthly Rent" value={fmtDollar(calc.monthlyRent)} />
                <MetricCard label="Monthly P&I" value={fmtDollar(Math.round(calc.monthlyPI))} />
                <MetricCard label="PITI Monthly" value={fmtDollar(Math.round(calc.pitiMonthly))} />
                <MetricCard label="DSCR Ratio" value={calc.dscrRatio.toFixed(2)} highlight />
              </div>
            </div>

            {/* Cash Flow Bars */}
            <div className="card mb-6">
              <div className="card-title"><span className="card-title-icon">📈</span> Monthly Cash Flow by Year</div>
              {(() => {
                const vals = calc.years.map((y) => y.monthlyCashFlow);
                const maxAbs = Math.max(...vals.map(Math.abs), 1);
                return (
                  <div className="cashflow-chart">
                    {calc.years.map((y) => {
                      const pct = Math.abs(y.monthlyCashFlow) / maxAbs;
                      const h = Math.max(pct * 90, 4);
                      const pos = y.monthlyCashFlow >= 0;
                      return (
                        <div key={y.yr} className="cf-bar-group">
                          <div className={cls("cf-bar-label-top", pos ? "cf-bar-pos" : "cf-bar-neg")}>
                            {fmtDollar(Math.round(y.monthlyCashFlow))}
                          </div>
                          <div
                            className={cls("cf-bar", pos ? "cf-bar--pos" : "cf-bar--neg")}
                            style={{ height: `${h}px` }}
                          />
                          <div className="cf-bar-year">Yr {y.yr}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Income & Expenses Table */}
            <div className="card mb-6">
              <div className="card-title"><span className="card-title-icon">📋</span> Income / Expenses</div>
              <YearTable years={calc.years} />
            </div>

            {/* Appreciation / ROI Table */}
            <div className="card mb-6">
              <div className="card-title"><span className="card-title-icon">🏦</span> Annual Appreciation / ROI</div>
              <AppreciationTable years={calc.years} />
            </div>

            {/* Return on Investment Summary */}
            <div className="card mb-6">
              <div className="card-title"><span className="card-title-icon">🎯</span> Return on Investment Summary</div>
              {(() => {
                const ti = calc.totalPropertyInvestment;
                const y = calc.years;
                const roiRows = [
                  {
                    label: "Cash on Cash ROI",
                    vals: [(y[0].cocROI + y[1].cocROI) / 2, y[2].cocROI, y[3].cocROI, y[4].cocROI],
                  },
                  {
                    label: "CoC + Principal Reduction",
                    vals: [(y[0].cocPlusPR + y[1].cocPlusPR) / 2, y[2].cocPlusPR, y[3].cocPlusPR, y[4].cocPlusPR],
                  },
                  {
                    label: "CoC + PR + Depreciation",
                    vals: [
                      ((y[0].grossProfit + y[0].annualPrincipalPaid + y[0].depreciation + y[1].grossProfit + y[1].annualPrincipalPaid + y[1].depreciation) / 2) / ti,
                      (y[2].grossProfit + y[2].annualPrincipalPaid + y[2].depreciation) / ti,
                      (y[3].grossProfit + y[3].annualPrincipalPaid + y[3].depreciation) / ti,
                      (y[4].grossProfit + y[4].annualPrincipalPaid + y[4].depreciation) / ti,
                    ],
                  },
                  {
                    label: "CoC + PR + Depreciation + Appreciation",
                    vals: [(y[0].totalROI + y[1].totalROI) / 2, y[2].totalROI, y[3].totalROI, y[4].totalROI],
                  },
                ];
                return (
                  <div className="year-table-wrapper">
                    <table className="year-table">
                      <thead>
                        <tr>
                          <th className="year-table-label-col">Metric</th>
                          <th className="year-table-year-col">Yr 1–2 Avg</th>
                          <th className="year-table-year-col">Year 3</th>
                          <th className="year-table-year-col">Year 4</th>
                          <th className="year-table-year-col">Year 5</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roiRows.map((row, ri) => (
                          <tr key={row.label} className={cls("year-row", ri % 2 === 0 && "year-row--even")}>
                            <td className="year-row-label">{row.label}</td>
                            {row.vals.map((v, vi) => (
                              <td key={vi} className="year-row-value">{fmtPct(v)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>

            <div className="disclaimer-box">
              <strong>Disclaimer</strong>
              <p>{DISCLAIMER_TEXT}</p>
            </div>

            <div style={{ textAlign: "center", marginTop: "24px" }} className="no-print">
              <button className="btn btn-primary" style={{ padding: "12px 36px", fontSize: 15 }} onClick={() => requestAccess("pdf")}>
                {exporting ? "Generating PDF..." : "⬇ Export as PDF"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══════════ LEAD CAPTURE MODAL ═══════════ */}
      {showLeadModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowLeadModal(false); }}>
          <div className="modal">

            {/* Modal Header */}
            <div className="modal-header">
              <img src={LOGO_SRC} alt="Traverse Home Loans" className="modal-logo" />
              <div className="modal-header-sub">Investment Property Proforma</div>
              <button className="modal-close" onClick={() => setShowLeadModal(false)}>✕</button>
            </div>

            {leadSubmitted ? (
              /* ── Thank You State ── */
              <div className="modal-thankyou">
                <div className="modal-thankyou-icon">✓</div>
                <div className="modal-thankyou-title">You're all set!</div>
                <div className="modal-thankyou-sub">
                  Thanks, <strong>{leadForm.name.split(" ")[0]}</strong>! Your full analysis is now unlocked.<br />
                  {pendingAction === "pdf" && "Your PDF is being prepared and will download shortly."}<br />
                  A member of the Traverse Home Loans team may reach out to discuss financing options for this deal.
                </div>
                <div className="modal-progress">
                  <div className="modal-progress-bar" />
                </div>
              </div>
            ) : (
              /* ── Form State ── */
              <div className="modal-body">
                <div className="modal-title">Unlock Your Analysis</div>
                <div className="modal-desc">
                  Enter your details below to unlock the full 5-year analysis and download your proforma PDF.
                </div>

                {inputs.address && (
                  <div className="modal-property-pill">
                    📍 {inputs.address}
                  </div>
                )}

                <form
                  onSubmit={(e) => { e.preventDefault(); submitLead(); }}
                  noValidate
                >
                  <div className="modal-field">
                    <label>Full Name</label>
                    <input
                      type="text"
                      placeholder="Jane Smith"
                      className={leadErrors.name ? "error" : ""}
                      value={leadForm.name}
                      onChange={(e) => setLeadForm(p => ({ ...p, name: e.target.value }))}
                      autoComplete="name"
                    />
                    {leadErrors.name && <span className="modal-error">{leadErrors.name}</span>}
                  </div>

                  <div className="modal-field">
                    <label>Email Address</label>
                    <input
                      type="email"
                      placeholder="jane@example.com"
                      className={leadErrors.email ? "error" : ""}
                      value={leadForm.email}
                      onChange={(e) => setLeadForm(p => ({ ...p, email: e.target.value }))}
                      autoComplete="email"
                    />
                    {leadErrors.email && <span className="modal-error">{leadErrors.email}</span>}
                  </div>

                  <div className="modal-field">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      placeholder="(555) 555-5555"
                      className={leadErrors.phone ? "error" : ""}
                      value={leadForm.phone}
                      onChange={(e) => setLeadForm(p => ({ ...p, phone: e.target.value }))}
                      autoComplete="tel"
                    />
                    {leadErrors.phone && <span className="modal-error">{leadErrors.phone}</span>}
                  </div>

                  <button type="submit" className="modal-submit" disabled={leadSubmitting}>
                    {leadSubmitting ? (
                      <span>Submitting...</span>
                    ) : (
                      <><span>🔓</span><span>{pendingAction === "pdf" ? "Unlock & Download PDF" : "Unlock My Analysis"}</span></>
                    )}
                  </button>

                  <label className="modal-consent">
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                    />
                    <span className="modal-consent-text">
                      By providing your phone number and email and submitting this form, you consent to be contacted by Traverse Home Loans via SMS text message and/or email. Message frequency may vary. Message and data rates may apply. Reply STOP to unsubscribe from SMS. Reply HELP for more information.{" "}
                      <a href="https://www.traversehomeloans.com/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)", textDecoration: "underline" }}>View our Privacy Policy.</a>
                    </span>
                  </label>
                  {leadErrors.consent && <span className="modal-consent-error">{leadErrors.consent}</span>}
                </form>
              </div>
            )}

          </div>
        </div>
      )}

      <footer className="site-footer no-print">
        <div className="site-footer-top">
          <div className="site-footer-brand">Traverse Home Loans</div>
          <div className="site-footer-nmls">
            <div className="footer-eq-housing">
              <div className="footer-eq-icon">&#8962;</div>
              <span>Equal Housing Opportunity</span>
            </div>
            <span>Co. <strong>NMLS #2560345</strong></span>
            <span>LO <strong>NMLS #1519991</strong></span>
            <span>AZ <strong>MB-2014481</strong></span>
            <span>AZ LO <strong>LO-2014493</strong></span>
          </div>
        </div>
        <div className="site-footer-disclaimer">
          This proforma analysis is based solely on information provided by the user and is offered by Traverse Home Loans for informational purposes only. Traverse Home Loans does not verify or guarantee the accuracy or completeness of any inputs, including but not limited to purchase price, rental income, expenses, interest rates, or appreciation assumptions. All projections and figures are estimates only and are subject to change without notice. This analysis does not constitute financial, legal, tax, or investment advice. Actual results may vary materially from the projections shown, and past performance is not indicative of future results. Real estate investing involves risk, and all investment decisions are made at the sole discretion of the user. Traverse Home Loans assumes no liability for any outcomes resulting from the use of this analysis. Traverse Home Loans is a licensed mortgage broker. This tool is intended solely to assist in evaluating potential financing scenarios and does not represent a loan commitment, pre-approval, or guarantee of financing. All loan approvals are subject to underwriting, creditworthiness, property appraisal, and lender-specific requirements. By downloading or using this report, you acknowledge and agree to the terms outlined in this disclaimer.
        </div>
      </footer>
    </>
  );
}
